import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback, useRef } from 'react';
import { User, CourseType, Activity, TopicMastery, Title, CourseState, Recommendation, SessionMode, Question, AppNotification, UnitContent, SubTopic, UserInsights, SubmitAttemptParams, SubmitAttemptResult, ReviewQueueItem, SubTopicProgress, UserSectionProgress, UserPrestige } from './types';
import { INITIAL_USER, INITIAL_ACTIVITIES, INITIAL_RADAR_DATA, INITIAL_LINE_DATA, INITIAL_COURSES, PRACTICE_QUESTIONS, INITIAL_NOTIFICATIONS, COURSE_CONTENT_DATA, COURSE_TOPICS } from './constants';
import { supabase } from './src/services/supabaseClient';
import { notificationsApi, contentApi, questionsApi, sectionsApi, practiceApi, usersApi } from './src/services/api';

interface AppContextType {
    user: User;
    isAuthenticated: boolean;
    isAuthLoading: boolean;
    activities: Activity[];
    radarData: TopicMastery[];
    lineData: { day: string; value: number }[];
    accuracyHistory: { date: string; accuracy: number; totalAttempts: number }[];
    courses: Record<CourseType, CourseState>;
    recommendation: Recommendation;
    hasDismissedLoginPrompt: boolean;
    questions: Question[]; // Dynamic Question Bank
    isCreatorAuthenticated: boolean; // Creator Access State
    topicContent: Record<string, UnitContent>; // Dynamic Content Data
    skills: { id: string; name: string; unit: string; prerequisites: string[] }[]; // Skills for Question Editor
    sections: Record<string, any[]>; // Sections by topic_id for Chapter Settings
    userInsights: UserInsights | null; // Agent Insight data
    reviewQueue: ReviewQueueItem[]; // Spaced repetition queue
    newlyUnlockedTitle: Title | null; // For achievement celebration
    setNewlyUnlockedTitle: (title: Title | null) => void;
    availableTitles: Title[];
    isPro: boolean;
    showPaywall: boolean;
    setShowPaywall: (show: boolean) => void;

    // Points Economy
    userPoints: { balance: number; lifetimeEarned: number };
    pointsBalanceRef: React.RefObject<HTMLElement>;
    awardPoints: (amount: number, type: string, sourceId?: string, description?: string, idempotencyKey?: string, x?: number, y?: number, suppressAnimation?: boolean) => Promise<{ success: boolean; newBalance?: number }>;
    triggerCoinAnimation: (amount: number, x?: number, y?: number, mode?: 'earn' | 'spend') => void;
    redeemProWithPoints: () => Promise<{ success: boolean; reason?: string; newBalance?: number; shortfall?: number }>;
    performDailyCheckin: () => Promise<{ success: boolean; streakDay?: number; basePoints?: number; bonusPoints?: number; totalPoints?: number; isMilestone?: boolean; reason?: string }>;
    recordLoginStreak: () => Promise<{ success: boolean; streak?: number; points?: number; reason?: string }>;
    getCheckinStatus: () => Promise<{ checkedInToday: boolean; currentStreak: number; monthCheckins: number; monthCalendar: any[]; repairCost?: number }>;
    fetchUserPoints: () => Promise<void>;
    spendPoints: (amount: number, type: string) => Promise<{ success: boolean; newBalance?: number; reason?: string }>;
    fetchGameStats: (gameId: string) => Promise<any>;
    saveGameStats: (gameId: string, updates: { high_score?: number; coins_earned?: number; stardust_earned?: number }) => Promise<boolean>;

    login: (email: string, username?: string, id?: string, subscriptionTier?: 'basic' | 'pro', subscriptionPeriodEnd?: string, hasSeenProIntro?: boolean, avatarUrl?: string, hasClaimedWelcomeGift?: boolean) => void;
    logout: () => Promise<void>;
    toggleCourse: (course: CourseType) => void;
    startCourse: (course: CourseType) => void;
    updateUser: (updates: Partial<User>) => void;
    claimFreePro: () => Promise<boolean>;
    recentPointsTransaction: { amount: number; description: string } | null;
    markProIntroSeen: () => Promise<void>;
    completePractice: (results: { correct: number; total: number; topic: string; isReview?: boolean; newlyCorrectFirstAttempts?: number }) => void;
    setSessionMode: (mode: SessionMode) => void;
    setRecommendationTopic: (topicId: string) => void;
    lockPracticeMode: (mode: 'Adaptive' | 'Review' | 'Random') => Promise<void>;
    isModeLocked: boolean;
    lockedModeExpiry: string | null;
    dismissLoginPrompt: () => void;

    // Creator Area Methods
    addQuestion: (q: Partial<Question> & { options: any[] }) => Promise<boolean>;
    updateQuestion: (q: Question) => Promise<boolean>;
    deleteQuestion: (id: string) => void;
    updateTopic: (unitId: string, subTopicId: string | null, data: any) => void;
    updateSection: (topicId: string, sectionId: string, data: any) => Promise<void>;
    verifyAccessCode: (code: string) => Promise<{ success: boolean; message?: string }>;

    navRedDots: {
        dashboard: boolean;
        practice: boolean;
        analysis: boolean;
        forum: number;
        settings: boolean;
        subscription: boolean;
        checkin: boolean;
    };
    fetchBadgeStatus: () => Promise<void>;
    markBadgeAsRead: (type: 'analysis' | 'forum' | 'practice') => Promise<void>;
    clearBadgeLocally: (type: keyof AppContextType['navRedDots']) => void;
    acceptFriendRequest: (senderId: string) => Promise<{ success: boolean; message?: string }>;
    sendGlobalBroadcast: (event: string, payload: any) => void;
    // Helper for Dashboard
    getCourseMastery: (course: CourseType) => number;
    getSectionsForTopic: (topicId: string) => any[];
    fetchSections: () => Promise<void>;
    fetchQuestions: () => Promise<void>;

    // Agent Insight Methods
    submitAttempt: (params: SubmitAttemptParams) => Promise<SubmitAttemptResult>;
    getUserInsights: () => Promise<UserInsights | null>;
    getReviewQueue: (limit?: number) => Promise<ReviewQueueItem[]>;
    toggleQuestionStarred: (questionId: string) => Promise<boolean>;
    toggleQuestionFlagged: (questionId: string) => Promise<boolean>;
    getTopicProgress: (topicId: string) => Promise<Record<string, SubTopicProgress>>;
    fetchAllUserProgress: () => Promise<void>;

    // Session Persistence Methods
    // Session Persistence Methods
    saveSectionProgress: (sectionId: string, data: any, stats?: { completed: number; total: number; score: number }, entityType?: 'course' | 'unit' | 'section' | 'algorithmic', skipStatusUpdate?: boolean) => Promise<boolean>;
    completeSectionSession: (sectionId: string, score: number, totalQuestions: number, correctQuestions: number, data: any, entityType?: 'course' | 'unit' | 'section' | 'algorithmic', skipStatusUpdate?: boolean) => Promise<boolean>;
    getUnitProgress: (unitId: string) => Promise<any>;
    getCourseProgress: (courseScope: string) => Promise<any>;
    getSectionProgress: (sectionId: string) => Promise<UserSectionProgress | null>;
    getTopicSectionProgress: (topicId: string) => Promise<UserSectionProgress[]>;
    // New synchronous access to cached progress
    sectionProgressMap: Record<string, UserSectionProgress>;
    getSectionStatus: (sectionId: string) => 'not_started' | 'in_progress' | 'completed';
    getSectionProgressData: (sectionId: string) => UserSectionProgress | undefined;
    resetSectionProgress: (sectionId: string) => Promise<boolean>;
    softResetSectionProgress: (sectionId: string) => Promise<boolean>;
    sectionTimes: Record<string, number>;
    incorrectQuestionIds: Set<string>;
    fetchIncorrectQuestions: () => Promise<void>;
    diagSupabase: () => Promise<void>;

    // New Activity Tracking
    logUserActivity: (params: { sectionId: string; attemptType: 'first_attempt' | 'review'; score: number; correctCount: number; totalQuestions: number; data: any }) => Promise<string | null>;
    getUserActivities: (sectionId: string) => Promise<any[]>;
    fetchAccuracyHistory: (range: '1W' | '1M' | '1Y' | 'ALL') => Promise<void>;
    friendIds: Set<string>;
    fetchFriends: () => Promise<void>;
    unreadCounts: Record<string, number>;
    clearUnread: (id: string) => void;
    checkinStatus: 'checked_in' | 'not_checked_in' | 'loading';
    refreshCheckinStatus: () => Promise<void>;

    // Modal Orchestration
    isStreakModalOpen: boolean;
    setIsStreakModalOpen: (isOpen: boolean) => void;

    // Prestige System
    userPrestige: UserPrestige | null;
    fetchUserPrestige: () => Promise<void>;
    purchaseStardust: (amountCoins: number) => Promise<{ success: boolean; message?: string }>;
    injectStardust: (amount: number) => Promise<{ success: boolean; message?: string }>;

    wrongAnswersCache: { items: any[], lastFetched: number };
    fetchWrongAnswersCache: () => Promise<void>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

// Helper for simulating Backend ID generation
const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

// Mode-dependent weight profiles for the enterprise scoring engine
export const getModeWeights = (mode: SessionMode) => {
    switch (mode) {
        case 'Review':
            return { masteryGap: 0.15, recencyDecay: 0.35, coverageGap: 0.10, sequentialBonus: 0.05, weakBoost: 0.35 };
        case 'Random':
            return { masteryGap: 0.20, recencyDecay: 0.15, coverageGap: 0.25, sequentialBonus: 0.15, weakBoost: 0.25 };
        default: // Adaptive
            return { masteryGap: 0.30, recencyDecay: 0.20, coverageGap: 0.15, sequentialBonus: 0.15, weakBoost: 0.20 };
    }
};

export const AppProvider = ({ children }: React.PropsWithChildren) => {
    const [user, setUser] = useState<User>(() => {
        // Safe check for window
        if (typeof window !== 'undefined') {
            const savedCourse = localStorage.getItem('user_course_mode');
            const cachedUser = localStorage.getItem('user_profile_cache');

            let baseUser = { ...INITIAL_USER };
            if (cachedUser) {
                try {
                    baseUser = { ...baseUser, ...JSON.parse(cachedUser) };
                } catch (e) { console.error('Error parsing user cache', e); }
            }

            return {
                ...baseUser,
                currentCourse: (savedCourse === 'AB' || savedCourse === 'BC') ? (savedCourse as CourseType) : baseUser.currentCourse
            };
        }
        return INITIAL_USER;
    });
    const [isAuthenticated, setIsAuthenticated] = useState(() => {
        if (typeof window !== 'undefined') {
            return localStorage.getItem('user_profile_cache') !== null;
        }
        return false;
    }); // Optimistic auth from cache
    const [isAuthLoading, setIsAuthLoading] = useState(() => {
        if (typeof window !== 'undefined') {
            return localStorage.getItem('user_profile_cache') === null;
        }
        return true;
    });
    // Initialize from localStorage so the prompt state persists across page refreshes
    // Use sessionStorage so popup appears once per browser session (closing tab resets it)
    const [availableTitles, setAvailableTitles] = useState<Title[]>([]);
    const [hasDismissedLoginPrompt, setHasDismissedLoginPrompt] = useState(() => {
        if (typeof window !== 'undefined') {
            return sessionStorage.getItem('hasDismissedLoginPrompt') === 'true';
        }
        return false;
    });
    const [isCreatorAuthenticated, setIsCreatorAuthenticated] = useState(false);

    // Dynamic Data States
    const [activities, setActivities] = useState<Activity[]>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem('activities_cache');
            return saved ? JSON.parse(saved) : INITIAL_ACTIVITIES;
        }
        return INITIAL_ACTIVITIES;
    });
    const [radarData, setRadarData] = useState<TopicMastery[]>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem('radar_data_cache');
            return saved ? JSON.parse(saved) : INITIAL_RADAR_DATA;
        }
        return INITIAL_RADAR_DATA;
    });
    const [incorrectQuestionIds, setIncorrectQuestionIds] = useState<Set<string>>(new Set());
    const [lineData, setLineData] = useState(INITIAL_LINE_DATA);
    const [accuracyHistory, setAccuracyHistory] = useState<{ date: string; accuracy: number; totalAttempts: number }[]>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem('accuracy_history_cache');
            return saved ? JSON.parse(saved) : [];
        }
        return [];
    });
    const [courses, setCourses] = useState(INITIAL_COURSES);

    // Lifted Question State
    const [questions, setQuestions] = useState<Question[]>(PRACTICE_QUESTIONS);

    // Lifted Topic Content State
    const [topicContent, setTopicContent] = useState<Record<string, UnitContent>>(COURSE_CONTENT_DATA);

    // Lifted Error Notebook Cache for 0-latency loading
    const [wrongAnswersCache, setWrongAnswersCache] = useState<{ items: any[], lastFetched: number }>({ items: [], lastFetched: 0 });

    // Persistent Red Dots State (Fetched from Backend RPC)
    const [navRedDots, setNavRedDots] = useState({
        dashboard: false,
        practice: false,
        analysis: false,
        forum: 0,
        settings: false,
        subscription: false,
        checkin: false
    });

    // Fetch Badges from Backend dynamically via RPC
    const fetchBadgeStatus = async () => {
        if (!user.id) return;
        try {
            const localDate = new Date().toLocaleDateString('en-CA');
            const [badgeRes, pendingRes] = await Promise.all([
                supabase.rpc('get_user_badges', { p_user_id: user.id, p_client_date: localDate }),
                supabase.from('friend_requests').select('*', { count: 'exact', head: true }).eq('receiver_id', user.id).eq('status', 'pending')
            ]);
            
            const { data, error } = badgeRes;
            const { count: pendingCount } = pendingRes;
            
            if (error) {
                console.error('Error fetching badge status dynamically:', error);
                return;
            }

            if (data) {
                setNavRedDots(prev => ({
                    dashboard: data.dashboard,
                    practice: data.practice,
                    analysis: data.analysis,
                    forum: (data.forum || 0) + (pendingCount || 0),
                    settings: data.settings,
                    subscription: data.settings,
                    checkin: data.dashboard // Assuming checkin inside Dashboard follows the same badge
                }));

                if (data.forum_unread_details) {
                    setUnreadCounts(data.forum_unread_details);
                    // Also persist it to localStorage in case we want immediate access on refresh before RPC finishes
                    localStorage.setItem('global_unread_counts', JSON.stringify(data.forum_unread_details));
                }

                if (data.has_claimed_welcome_gift) {
                    updateUser({ hasClaimedWelcomeGift: true });
                }
            }
        } catch (err) {
            console.error('Error fetching badge status dynamically:', err);
        }
    };

    const markBadgeAsRead = async (type: 'analysis' | 'forum' | 'practice') => {
        if (!user.id) return;
        // Immediately clear local badge so UI feels instant
        setNavRedDots(prev => ({ ...prev, [type]: type === 'forum' ? 0 : false }));
        try {
            const now = new Date().toISOString();
            const updates: any = {};
            
            if (type === 'analysis') {
                updates.last_analysis_view_time = now;
            }
            if (type === 'forum') updates.last_forum_read_time = now;
            if (type === 'practice') updates.last_practice_rec_view_time = now;

            if (Object.keys(updates).length > 0) {
                const { error } = await supabase.from('user_profiles').update(updates).eq('id', user.id);
                if (error) throw error;
            }
            
            // For analysis: skip fetchBadgeStatus to prevent race condition.
            // The in-memory ref above protects against concurrent fetchBadgeStatus calls.
            if (type !== 'analysis') {
                await fetchBadgeStatus();
            }
        } catch (err) {
            console.error('Error marking badge as read:', err);
        }
    };

    const clearBadgeLocally = (type: keyof AppContextType['navRedDots']) => {
        setNavRedDots(prev => ({ ...prev, [type]: type === 'forum' ? 0 : false }));
    };

    // Skills data for Question Editor
    const [skills, setSkills] = useState<{ id: string; name: string; unit: string; prerequisites: string[] }[]>([]);

    // Sections data from database (keyed by topic_id)
    const [sections, setSections] = useState<Record<string, any[]>>({});
    const [friendIds, setFriendIds] = useState<Set<string>>(new Set());

    // Cache for section progress to prevent UI lag
    const [sectionProgressMap, setSectionProgressMap] = useState<Record<string, UserSectionProgress>>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem('section_progress_cache');
            return saved ? JSON.parse(saved) : {};
        }
        return {};
    });

    const [userInsights, setUserInsights] = useState<UserInsights | null>(null);
    const [reviewQueue, setReviewQueue] = useState<ReviewQueueItem[]>([]);
    const [newlyUnlockedTitle, setNewlyUnlockedTitle] = useState<Title | null>(null);
    const [showPaywall, setShowPaywall] = useState(false);

    // Points Economy State
    // Points Economy State
    const [userPoints, setUserPoints] = useState<{ balance: number; lifetimeEarned: number }>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem('user_points_cache');
            return saved ? JSON.parse(saved) : { balance: 0, lifetimeEarned: 0 };
        }
        return { balance: 0, lifetimeEarned: 0 };
    });
    const pointsBalanceRef = React.useRef<HTMLElement>(null);
    const [userPrestige, setUserPrestige] = useState<UserPrestige | null>(null);

    const [recentPointsTransaction, setRecentPointsTransaction] = useState<{ amount: number; description: string } | null>(null);
    const [checkinStatus, setCheckinStatus] = useState<'checked_in' | 'not_checked_in' | 'loading'>('loading');
    const [isStreakModalOpen, setIsStreakModalOpen] = useState(false);

    useEffect(() => {
        if (checkinStatus === 'not_checked_in') {
            setNavRedDots(prev => ({ ...prev, dashboard: true, checkin: true }));
        } else if (checkinStatus === 'checked_in') {
            setNavRedDots(prev => ({ ...prev, dashboard: false, checkin: false }));
        }
    }, [checkinStatus]);

    const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem('global_unread_counts');
            return saved ? JSON.parse(saved) : {};
        }
        return {};
    });

    const clearUnread = async (id: string) => {
        // 1. Update local state
        setUnreadCounts(prev => {
            if (!prev[id]) return prev;
            const next = { ...prev };
            delete next[id];
            localStorage.setItem('global_unread_counts', JSON.stringify(next));
            return next;
        });

        // 2. Clear from database if user is authenticated
        if (user?.id) {
            try {
                // Determine if ID is for a channel or chat
                // In our current system, we store chat_id or channel_id in the notifications table
                await supabase
                    .from('notifications')
                    .update({ unread: false })
                    .match({ user_id: user.id, unread: true })
                    .or(`chat_id.eq.${id},channel_id.eq.${id}`);

                // Refresh counts from backend to ensure navRedDots.forum is perfectly synced
                await fetchBadgeStatus();
            } catch (err) {
                console.error('Failed to clear notifications in DB:', err);
            }
        }
    };

    const globalChannelRef = useRef<any>(null);

    // Global Message Subscriptions for Unread Badges
    useEffect(() => {
        if (!user?.id || !isAuthenticated) return;

        const handleNewMsg = (payload: any, type: 'channel' | 'dm') => {
            const newMsg = payload.new;
            const targetId = type === 'channel' ? newMsg.channel_id : newMsg.chat_id;

            // We don't know the "active" chat here easily without passing it up, 
            // but we can increment it always and Forum.tsx will clear it.
            // Or better: The Forum component already manages active IDs. 
            // Let's just increment if it's not from us.
            if (newMsg.user_id !== user.id) {
                setUnreadCounts(prev => {
                    const next = {
                        ...prev,
                        [targetId]: (prev[targetId] || 0) + 1
                    };
                    localStorage.setItem('global_unread_counts', JSON.stringify(next));
                    return next;
                });
                // Increment top navbar forum badge locally
                setNavRedDots(prev => ({
                    ...prev,
                    forum: prev.forum + 1
                }));
            }
        };

        const channel = supabase.channel('global-chat-notifications')
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'forum_messages' }, (payload) => handleNewMsg(payload, 'channel'))
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'direct_messages' }, (payload) => handleNewMsg(payload, 'dm'))
            .on('broadcast', { event: 'new_dm' }, (payload) => handleNewMsg({ new: payload.payload }, 'dm'))
            .on('broadcast', { event: 'new_channel_msg' }, (payload) => handleNewMsg({ new: payload.payload }, 'channel'))
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications' }, (payload) => {
                if (payload.new.user_id === user.id && payload.new.unread) {
                    fetchBadgeStatus();
                }
            })
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'activities' }, (payload) => {
                if (payload.new.user_id === user.id) {
                    fetchBadgeStatus();
                }
            })
            .subscribe((status) => {
                if (status === 'SUBSCRIBED') {
                    globalChannelRef.current = channel;
                }
            });

        return () => {
            globalChannelRef.current = null;
            supabase.removeChannel(channel);
        };
    }, [user?.id, isAuthenticated]);

    const sendGlobalBroadcast = (event: string, payload: any) => {
        if (globalChannelRef.current) {
            globalChannelRef.current.send({
                type: 'broadcast',
                event: event,
                payload: payload
            });
        }
    };

    // Global Midnight Refresh Interval
    useEffect(() => {
        if (!isAuthenticated) return;
        
        let lastDate = new Date().toLocaleDateString('en-CA');
        
        const checkMidnight = () => {
            const currentDate = new Date().toLocaleDateString('en-CA');
            if (currentDate !== lastDate) {
                console.log('Midnight Rollover Detected!');
                lastDate = currentDate;
                fetchBadgeStatus();
                refreshCheckinStatus();
            }
        };

        const interval = setInterval(checkMidnight, 60000);
        window.addEventListener('focus', checkMidnight);
        
        return () => {
            clearInterval(interval);
            window.removeEventListener('focus', checkMidnight);
        };
    }, [user?.id, isAuthenticated]);

    // Handle Daily Login Streak (Run once per session/user load)
    useEffect(() => {
        if (!user?.id) return;

        const checkLoginStreak = async () => {
            try {
                // Determine timezone
                const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                const { data, error } = await supabase.rpc('handle_daily_login', {
                    user_uuid: user.id,
                    p_timezone: tz
                });

                if (error) throw error;
                if (data) {
                    console.log('🔥 Daily Login processed:', data);
                }
            } catch (error) {
                console.error('Failed to process daily login:', error);
            }
        };

        checkLoginStreak();
    }, [user?.id]);

    // Algorithmic Recommendation State - Initial State for New User
    const [recommendation, setRecommendation] = useState<Recommendation>({
        topic: 'Limits',
        reason: 'Start your AP Calculus journey with the fundamentals.',
        currentMastery: 0,
        targetMastery: 80,
        mode: 'Adaptive',
        hasData: false
    });

    // Fetch all sections from database and merge into topicContent for Practice display
    const fetchSections = async () => {
        try {
            console.log('🔄 Fetching sections...');
            const data = await sectionsApi.getSections();

            // Group by topic_id
            const grouped: Record<string, any[]> = {};
            data.forEach((section: any) => {
                // Standardize topic_id: Map legacy ABBC_ to Both_ to match frontend constants
                let topicKey = section.topic_id;
                let originalKey = section.topic_id; // TRACK ORIGINAL

                if (topicKey.startsWith('ABBC_')) {
                    topicKey = 'Both_' + topicKey.substring(5);
                } else if (topicKey.startsWith('AB_') || topicKey.startsWith('BC_')) {
                    // Optional: Handle other legacy formats if needed, but ABBC is the main culprit
                    // Ensure we don't break strict AB/BC units, but normally shared is Both
                    // For now, only fix ABBC -> Both as that is the confirmed mismatch
                }

                if (!grouped[topicKey]) {
                    grouped[topicKey] = [];
                }
                grouped[topicKey].push(section);

                // Debug log for one specific mapping to see if it works
                if (originalKey.includes('ABBC_Limits')) {
                    console.log(`[AppContext] Mapped ${originalKey} -> ${topicKey}`);
                }
            });
            console.log(`[AppContext] Grouped sections keys:`, Object.keys(grouped));
            setSections(grouped);

            // Also update topicContent with sections data for Practice display
            setTopicContent(prev => {
                const updated = { ...prev };

                Object.keys(grouped).forEach(topicId => {
                    if (!updated[topicId]) return;

                    const topicSections = grouped[topicId];

                    // Find unit_test section and update unitTest config
                    const unitTestSection = topicSections.find(s => s.id === 'unit_test');
                    if (unitTestSection) {
                        updated[topicId] = {
                            ...updated[topicId],
                            unitTest: {
                                title: unitTestSection.title || 'Unit Test',
                                description: unitTestSection.description || '',
                                description_2: unitTestSection.description_2 || null,
                                estimatedMinutes: unitTestSection.estimated_minutes || 45
                            }
                        };
                    }

                    // Find overview section (Unit Settings) and update topic config
                    const overviewSection = topicSections.find(s => s.id === 'overview');
                    if (overviewSection) {
                        updated[topicId] = {
                            ...updated[topicId],
                            title: overviewSection.title,
                            description: overviewSection.description
                        };
                    }

                    // Update subTopics with section data (excluding unit_test)
                    const subSections = topicSections.filter(s => s.id !== 'unit_test');

                    // CRITICAL FIX: If subTopics is empty in state, use static data as the base
                    // This prevents optimistic overwrite of subTopics if the DB doesn't return all of them
                    const baseSubTopics = (updated[topicId].subTopics && updated[topicId].subTopics.length > 0)
                        ? updated[topicId].subTopics
                        : COURSE_CONTENT_DATA[topicId]?.subTopics || [];

                    if (subSections.length > 0 && baseSubTopics.length > 0) {
                        updated[topicId] = {
                            ...updated[topicId],
                            subTopics: baseSubTopics.map((sub: any) => {
                                // Relaxed find: Check for string vs number ID mismatch
                                const dbSection = subSections.find(s => String(s.id) === String(sub.id));
                                if (dbSection) {

                                    return {
                                        ...sub,
                                        title: dbSection.title ?? sub.title,
                                        description: dbSection.description ?? sub.description,
                                        // Mapped to description_2
                                        description_2: dbSection.description_2 || dbSection.chapter_detailed_description || dbSection.description2 || dbSection.detailed_description || sub.description_2 || null,
                                        courseScope: dbSection.course_scope || sub.courseScope,
                                        estimatedMinutes: dbSection.estimated_minutes || sub.estimatedMinutes,
                                        hasLesson: dbSection.has_lesson !== false,
                                        hasPractice: dbSection.has_practice !== false
                                    };
                                }
                                return sub;
                            })
                        };
                    }
                });

                // Debug verification key check
                const sampleKey = Object.keys(grouped)[0];
                if (sampleKey && updated[sampleKey]) {
                    console.log(`DEBUG: Merged content for ${sampleKey}. Subtopics count: ${updated[sampleKey].subTopics?.length}`);
                }

                return updated;
            });

            console.log(`✅ Loaded ${data.length} sections`);



            console.log('✅ Updated topicContent with DB sections');
        } catch (error: any) {
            console.error('❌ Failed to fetch sections from backend:', error);
            // Provide visual feedback if we're in development
            if (window.location.hostname === 'localhost') {
                console.warn('⚠️ LOCALHOST SYNC ERROR: Ensure backend is running on port 4005');
            }
        }
    };

    // Calculate Unit Mastery for Radar Chart
    const fetchRadarData = async () => {
        if (!user || !user.id) return;

        // Definition of Units for Radar Axis (AB & BC)
        const UNIT_MAPPING: { id: string; label: string }[] = [
            { id: 'Both_Limits', label: 'Unit 1' },
            { id: 'Both_Derivatives', label: 'Unit 2' },
            { id: 'Both_Composite', label: 'Unit 3' },
            { id: 'Both_Applications', label: 'Unit 4' },
            { id: 'Both_Analytical', label: 'Unit 5' },
            { id: 'Both_Integration', label: 'Unit 6' },
            { id: 'Both_DiffEq', label: 'Unit 7' },
            { id: 'Both_AppIntegration', label: 'Unit 8' },
            { id: 'BC_Unit9', label: 'Unit 9' },
            { id: 'BC_Series', label: 'Unit 10' }
        ];

        try {
            const unitIds = UNIT_MAPPING.map(u => u.id);
            const { data, error } = await supabase.rpc('get_unit_scores', {
                p_unit_ids: unitIds
            });

            if (error) throw error;

            const scoresMap: Record<string, number> = {};
            if (data) {
                data.forEach((row: any) => {
                    // Use the database-calculated mastery score (which already handles 85% threshold and 50/50 split)
                    scoresMap[row.unit_id] = Number(row.score) || 0;
                });
            }

            const newRadarData = UNIT_MAPPING.map(u => ({
                subject: u.label,
                A: Math.round(scoresMap[u.id] || 0), // Use calculated mastery or 0
                fullMark: 100
            }));

            setRadarData(newRadarData);
            localStorage.setItem('radar_data_cache', JSON.stringify(newRadarData));

            // Sync recommendation with current mastery if topic matches
            setRecommendation(prev => {
                const cleanRecTopic = prev.topic.includes('_') ? prev.topic.split('_')[1] : prev.topic;
                const matchingUnit = UNIT_MAPPING.find(u => u.id.includes(cleanRecTopic) || u.label.includes(cleanRecTopic));
                if (matchingUnit) {
                    const latestScore = Math.round(scoresMap[matchingUnit.id] || 0);
                    return { ...prev, currentMastery: latestScore };
                }
                return prev;
            });
        } catch (error) {
            console.error('fetchRadarData error:', error);
        }
    };

    // Calculate Performance Trend (Daily Mastery Index)
    const fetchLineData = async () => {
        if (!user || !user.id) return;

        try {
            // We want last 7 days including today
            const days = [];
            for (let i = 6; i >= 0; i--) {
                const d = new Date();
                d.setDate(d.getDate() - i);
                days.push(d);
            }

            // Fetch stats for each day (inefficient but simple for now, or use a better RPC if available)
            // For "Daily Mastery Index", we can use adherence or score. 
            // Let's use `get_daily_user_stats` for Today, and mock or fetch history if needed.
            // Currently `get_daily_user_stats` gets aggregate since timestamp.

            // BETTER APPROACH:
            // Since we don't have a rigid "daily history" table, we can query `question_attempts` grouped by day.
            // But to save RPC complexity, let's just show "Today" accurately and keep others static or simple for now, 
            // unless we want to write a complex history RPC.
            // The requirement is "Performance Trend".
            // Let's use a new RPC or just query attempts. Since we crave accuracy:

            const { data, error } = await supabase.rpc('get_daily_user_stats', { p_start_timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString() });

            if (error) throw error;

            const todayStats = data?.[0]; // { total_time_seconds, unique_questions_solved, correct_attempts, total_attempts }

            // Calculate "Mastery Index" for today (simple formula: Accuracy * Volume factor)
            // This is arbitrary but valid for a dashboard metric
            let todayValue = 0;
            if (todayStats && todayStats.total_attempts > 0) {
                const accuracy = (todayStats.correct_attempts / todayStats.total_attempts) * 100;
                // Cap it at 100, maybe scale by volume? For now just show Accuracy.
                todayValue = Math.round(accuracy);
            }

            setLineData(prev => {
                const next = [...prev];
                // Update "Today" (last element)
                next[next.length - 1] = { ...next[next.length - 1], value: todayValue };
                return next;
            });

        } catch (error) {
            console.error('fetchLineData error:', error);
        }
    };

    // Fetch Recent Activities (Global)
    const fetchRecentActivities = async () => {
        if (!isAuthenticated || !user?.id) return;

        try {
            const { data, error } = await supabase
                .from('activities')
                .select('*')
                .eq('user_id', user.id)
                .order('created_at', { ascending: false })
                .limit(10);

            if (error) {
                console.error('fetchRecentActivities failed:', error);
                return;
            };

            if (data) {
                const mappedActivities: Activity[] = data.map((d: any) => ({

                    // Activity type 'id' is number in types.ts? Let's check constraints.
                    // If types.ts says number, we might need to change it or hash the UUID.
                    // Workaround: Use timestamp as ID for frontend key if unique enough, or just cast to any.
                    // See types.ts content... id is number.
                    // Temporary fix: Use Date.parse(created_at) or hash for ID if strict, 
                    // BUT better to update the type definition to string | number.
                    // See types.ts content... id is number.
                    // Workaround: Use timestamp as ID for frontend key, plus random to avoid duplicates
                    id: new Date(d.created_at).getTime() + Math.random(),
                    type: d.type === 'practice' ? 'quiz' : 'practice', // Map to frontend types
                    title: d.title,
                    description: d.description,
                    timestamp: new Date(d.created_at).toLocaleDateString() === new Date().toLocaleDateString()
                        ? 'Today'
                        : new Date(d.created_at).toLocaleDateString(),
                    score: d.score
                }));
                setActivities(mappedActivities);
                localStorage.setItem('activities_cache', JSON.stringify(mappedActivities));
            }
        } catch (error) {
            console.error('fetchRecentActivities error:', error);
        }
    };

    // Fetch Accuracy History for Chart
    const fetchAccuracyHistory = async (range: '1W' | '1M' | '1Y' | 'ALL') => {
        if (!user || !user.id) return;

        let days = null;
        if (range === '1W') days = 7;
        else if (range === '1M') days = 30;
        else if (range === '1Y') days = 365;

        try {
            const { data, error } = await supabase.rpc('get_accuracy_history', { p_days: days });

            if (error) {
                console.error('get_accuracy_history RPC error:', error);
                return;
            }

            if (data) {
                const mapped = data.map((d: any) => ({
                    date: d.date,
                    accuracy: Number(d.accuracy),
                    totalAttempts: d.total_attempts
                }));
                setAccuracyHistory(mapped);
                localStorage.setItem('accuracy_history_cache', JSON.stringify(mapped));
            }
        } catch (err) {
            console.error('fetchAccuracyHistory error:', err);
        }
    };

    // Update a section in the database
    const updateSection = async (topicId: string, sectionId: string, data: any) => {
        try {
            // 1. Optimistic update to sections state
            setSections(prev => {
                const updated = { ...prev };
                if (updated[topicId]) {
                    const idx = updated[topicId].findIndex(s => s.id === sectionId);
                    if (idx !== -1) {
                        updated[topicId] = [...updated[topicId]];
                        updated[topicId][idx] = { ...updated[topicId][idx], ...data };
                    }
                }
                return updated;
            });

            // 2. Also update topicContent.subTopics so Practice area shows updated data
            setTopicContent(prev => {
                const unit = prev[topicId];
                if (!unit) return prev;

                // Handle unit_test separately
                if (sectionId === 'unit_test') {
                    return {
                        ...prev,
                        [topicId]: {
                            ...unit,
                            unitTest: {
                                ...unit.unitTest,
                                title: data.title ?? unit.unitTest?.title ?? 'Unit Test',
                                description: data.description ?? unit.unitTest?.description,
                                estimatedMinutes: data.estimated_minutes ?? unit.unitTest?.estimatedMinutes ?? 45
                            }
                        }
                    };
                }

                // Handle subTopics
                if (!unit.subTopics) return prev;

                const updatedSubTopics = unit.subTopics.map(sub => {
                    if (sub.id === sectionId) {
                        return {
                            ...sub,
                            title: data.title ?? sub.title,
                            description: data.description ?? sub.description,
                            description_2: data.description_2 ?? sub.description_2,
                            estimatedMinutes: data.estimated_minutes ?? sub.estimatedMinutes,
                            hasLesson: data.has_lesson ?? sub.hasLesson,
                            hasPractice: data.has_practice ?? sub.hasPractice
                        };
                    }
                    return sub;
                });

                return {
                    ...prev,
                    [topicId]: { ...unit, subTopics: updatedSubTopics }
                };
            });

            // 3. Call API to persist
            await sectionsApi.updateSection(topicId, sectionId, data);
            console.log(`✅ Section ${topicId}/${sectionId} saved to Supabase`);
        } catch (error) {
            console.error('Failed to update section:', error);
            throw error;
        }
    };

    // Helper to get sections for a topic (with fallback to COURSE_CONTENT_DATA)
    const getSectionsForTopic = (topicId: string): any[] => {
        if (sections[topicId] && sections[topicId].length > 0) {
            return sections[topicId];
        }
        // Fallback to constants data
        return COURSE_CONTENT_DATA[topicId]?.subTopics || [];
    };

    // Note: Old notification system has been removed. Badges are handled via fetchBadgeStatus().

    const acceptFriendRequest = async (senderId: string): Promise<{ success: boolean; message?: string }> => {
        if (!user.id) return { success: false, message: 'User not authenticated' };
        try {
            const { data, error } = await supabase.rpc('accept_friend_request', {
                p_sender_id: senderId,
                p_receiver_id: user.id
            });

            if (error) {
                console.error('accept_friend_request RPC error:', error);
                return { success: false, message: error.message };
            }

            if (data?.success) {
                // Refresh friend list
                fetchFriends();
                // We no longer manage local notifications array. Badges will be updated via polling.
                fetchBadgeStatus();
                return { success: true };
            } else {
                return { success: false, message: data?.message || 'Failed to accept friend request' };
            }
        } catch (err: any) {
            console.error('acceptFriendRequest error:', err);
            return { success: false, message: err.message };
        }
    };

    // --- Content Methods ---
    const fetchContent = async () => {
        try {
            console.log('🔄 Fetching course content...');
            // 1. Try to get content from DB
            let data = await contentApi.getTopics();

            // 2. If empty, auto-seed (DISABLED to prevent 400 Bad Request loops)
            if (!data || Object.keys(data).length === 0) {
                console.warn('⚠️ No content found returning from DB. Falling back to static data.');
                // await contentApi.seedContent();
                // data = await contentApi.getTopics();
            }

            if (data && Object.keys(data).length > 0) {
                // CRITICAL FIX: Merge backend data into static COURSE_CONTENT_DATA
                // instead of replacing entirely. The DB topic_content table may have
                // empty sub_topics arrays, which would wipe the static chapter definitions.
                setTopicContent(prev => {
                    const merged = { ...prev };
                    Object.keys(data).forEach(topicId => {
                        const dbTopic = data[topicId];
                        const staticTopic = merged[topicId] || COURSE_CONTENT_DATA[topicId];
                        if (!staticTopic) {
                            // New topic from DB that doesn't exist in static data
                            merged[topicId] = dbTopic;
                            return;
                        }
                        merged[topicId] = {
                            ...staticTopic,
                            // Override title/description from DB if available
                            title: dbTopic.title || staticTopic.title,
                            description: dbTopic.description || staticTopic.description,
                            unitTest: dbTopic.unitTest || staticTopic.unitTest,
                            // ONLY override subTopics if DB actually has non-empty data
                            subTopics: (dbTopic.subTopics && dbTopic.subTopics.length > 0)
                                ? dbTopic.subTopics
                                : staticTopic.subTopics || [],
                        };
                    });
                    return merged;
                });
                console.log('✅ Content merged from backend (preserved static subTopics)');
            }
        } catch (error) {
            console.error('Failed to sync content:', error);
            // Fallback to static data is already handled by initial state
        }
    };

    // --- Skills Methods ---
    const fetchSkills = async () => {
        try {
            const data = await contentApi.getSkills();
            if (data && data.length > 0) {
                setSkills(data);
                console.log('✅ Skills loaded:', data.length);
            }
        } catch (error) {
            console.error('Failed to fetch skills:', error);
        }
    };

    // --- Questions Methods (fetch from DB) ---
    const fetchQuestions = async () => {
        try {
            console.log('🔄 Fetching questions from database...');
            const data = await questionsApi.getQuestions({ limit: 10000 });

            if (data && data.length > 0) {
                // Merge with static questions, preferring DB versions for duplicates
                setQuestions(prev => {
                    const dbQuestionIds = new Set(data.map(q => q.id));
                    const staticOnly = prev.filter(q => !dbQuestionIds.has(q.id));
                    return [...data, ...staticOnly];
                });
                console.log('✅ Questions loaded from DB:', data.length);
                if (data.length === 0) {
                    console.log('ℹ️ Questions loaded from DB: empty');
                }
            } else {
                console.log('ℹ️ No questions in DB (empty array returned), using static data');
                if (data === null || data === undefined) {
                    console.warn('⚠️ data is null/undefined during fetchQuestions');
                }
            }
        } catch (error) {
            console.error('Failed to fetch questions:', error);
            // Keep static questions as fallback
        }
    };

    // --- Real-time Notifications Listener (Improved for DM background alerts) ---
    useEffect(() => {
        if (!user || !isAuthenticated || !user.id) return;

        let forumSubscription: any;
        let titleSub: any;

        const setupListener = async () => {
            // Subscribe to activities for forum red dots
            forumSubscription = supabase.channel('global-activities-v1')
                .on(
                    'postgres_changes',
                    { event: 'INSERT', schema: 'public', table: 'activities', filter: `user_id=eq.${user.id}` },
                    () => {
                        fetchBadgeStatus();
                    }
                )
                .subscribe();

            // Achievement unlocks
            titleSub = supabase.channel('achievement-unlocks')
                .on(
                    'postgres_changes',
                    {
                        event: 'INSERT',
                        schema: 'public',
                        table: 'user_titles',
                        filter: `user_id=eq.${user.id}`
                    },
                    async (payload: any) => {
                        const { title_id } = payload.new;
                        const { data } = await supabase
                            .from('titles')
                            .select('*')
                            .eq('id', title_id)
                            .single();
                        if (data) setNewlyUnlockedTitle(data as Title);
                    }
                )
                .subscribe();
        };

        setupListener();

        return () => {
            if (forumSubscription) supabase.removeChannel(forumSubscription);
            if (titleSub) supabase.removeChannel(titleSub);
        };
    }, [user.id, isAuthenticated]);

    // Memoized section times calculation for all pages to share
    const sectionTimes = useMemo(() => {
        const times: Record<string, number> = {};
        questions.forEach(q => {
            const sid = q.sectionId || q.subTopicId;
            if (!sid) return;
            // Map course properly (BC includes all AB content, AB only includes AB content)
            const isMatch = q.course === 'Both' ||
                (user.currentCourse === 'AB' && q.course === 'AB') ||
                (user.currentCourse === 'BC');

            if (!isMatch) return;

            times[sid] = (times[sid] || 0) + (q.targetTimeSeconds || 120);
        });
        return times;
    }, [questions, user.currentCourse]);





    // =====================================================
    // AGENT INSIGHT RPC FUNCTIONS
    // =====================================================

    const submitAttempt = async (params: SubmitAttemptParams): Promise<SubmitAttemptResult> => {
        try {
            const { data, error } = await supabase.rpc('submit_attempt', {
                p_question_id: params.questionId,
                p_is_correct: params.isCorrect,
                p_selected_option_id: params.selectedOptionId || null,
                p_answer_numeric: params.answerNumeric || null,
                p_time_spent_seconds: params.timeSpentSeconds,
                p_error_tags: params.errorTags,
                p_session_id: params.sessionId || null
            });

            if (error) {
                console.error('submit_attempt RPC error:', error);
                return { success: false, error: error.message };
            }

            console.log('✅ Attempt submitted via RPC:', data);

            // Refresh insights after submission
            getUserInsights();

            const result = {
                success: data?.success ?? false,
                attemptId: data?.attempt_id,
                attemptNo: data?.attempt_no,
                isCorrect: data?.is_correct,
                coinsAwarded: data?.coins_awarded ?? 0,
                error: data?.error
            };

            if (result.success) {
                if (params.isCorrect === false) {
                    setIncorrectQuestionIds(prev => {
                        const next = new Set(prev);
                        next.add(params.questionId);
                        return next;
                    });
                } else {
                    setIncorrectQuestionIds(prev => {
                        const next = new Set(prev);
                        next.delete(params.questionId);
                        return next;
                    });
                }
            }

            if (result.coinsAwarded && result.coinsAwarded > 0) {
                // Background fetch to sync balance
                fetchUserPoints();
            }

            return result as SubmitAttemptResult;
        } catch (error: any) {
            console.error('submitAttempt error:', error);
            return { success: false, error: error.message };
        }
    };

    const getUserInsights = async (): Promise<UserInsights | null> => {
        if (!isAuthenticated || !user?.id) return null;
        try {
            const { data, error } = await supabase.rpc('get_user_insights');

            if (error) {
                console.error('get_user_insights RPC error:', error);
                return null;
            }

            const insights: UserInsights = {
                stats: {
                    userId: user.id,
                    totalAttempts: data?.stats?.total_attempts ?? 0,
                    correctAttempts: data?.stats?.correct_attempts ?? 0,
                    accuracyRate: data?.stats?.accuracy_rate ?? 0,
                    uniqueQuestionsAttempted: data?.stats?.unique_questions ?? 0,
                    streakCorrect: data?.stats?.streak_correct ?? 0,
                    streakWrong: data?.stats?.streak_wrong ?? 0,
                    currentStreakDays: data?.stats?.streak_days ?? 0,
                    longestStreakDays: data?.stats?.longest_streak ?? 0,
                    totalTimeMinutes: data?.stats?.total_time_minutes ?? 0,
                    lastPracticed: data?.stats?.last_practiced ?? null
                },
                weakSkills: (data?.weak_skills ?? []).map((s: any) => ({
                    skillId: s.skill_id,
                    skillName: s.skill_name,
                    mastery: s.mastery,
                    confidence: s.confidence,
                    streakWrong: s.streak_wrong,
                    lastPracticed: null
                })),
                topErrors: (data?.top_errors ?? []).map((e: any) => ({
                    errorTagId: e.error_tag_id,
                    errorName: e.error_name,
                    category: e.category,
                    count: e.count
                })),
                reviewQueue: (data?.review_queue ?? []).map((r: any) => ({
                    questionId: r.question_id,
                    nextReviewAt: r.next_review_at,
                    reviewCount: r.review_count,
                    intervalDays: r.interval_days,
                    isOverdue: false
                })),
                recommendations: (data?.recommendations ?? []).map((rec: any) => ({
                    questionId: rec.question_id,
                    score: rec.score,
                    reason: rec.reason,
                    reasonDetail: rec.reason_detail,
                    skillId: rec.skill_id
                }))
            };

            setUserInsights(insights);
            console.log('✅ User insights loaded:', insights);
            return insights;
        } catch (error) {
            console.error('getUserInsights error:', error);
            return null;
        }
    };

    const getReviewQueue = async (limit: number = 20): Promise<ReviewQueueItem[]> => {
        try {
            const { data, error } = await supabase.rpc('get_review_queue', { p_limit: limit });

            if (error) {
                console.error('get_review_queue RPC error:', error);
                return [];
            }

            const queue: ReviewQueueItem[] = (data ?? []).map((item: any) => ({
                questionId: item.question_id,
                nextReviewAt: item.next_review_at,
                reviewCount: item.review_count,
                intervalDays: item.interval_days,
                isOverdue: item.is_overdue,
                questionTopic: item.question_topic,
                questionPrompt: item.question_prompt
            }));

            setReviewQueue(queue);
            return queue;
        } catch (error) {
            console.error('getReviewQueue error:', error);
            return [];
        }
    };

    const toggleQuestionStarred = async (questionId: string): Promise<boolean> => {
        try {
            const { data, error } = await supabase.rpc('toggle_question_starred', {
                p_question_id: questionId
            });

            if (error) {
                console.error('toggle_question_starred RPC error:', error);
                return false;
            }

            console.log('✅ Question starred toggled:', data);
            return data ?? false;
        } catch (error) {
            console.error('toggleQuestionStarred error:', error);
            return false;
        }
    };

    const toggleQuestionFlagged = async (questionId: string): Promise<boolean> => {
        try {
            const { data, error } = await supabase.rpc('toggle_question_flagged', {
                p_question_id: questionId
            });

            if (error) {
                console.error('toggle_question_flagged RPC error:', error);
                return false;
            }

            console.log('✅ Question flagged toggled:', data);
            return data ?? false;
        } catch (error) {
            console.error('toggleQuestionFlagged error:', error);
            return false;
        }
    };

    const getTopicProgress = async (topicId: string): Promise<Record<string, SubTopicProgress>> => {
        try {
            const { data, error } = await supabase.rpc('get_topic_progress', {
                p_topic_id: topicId,
                p_user_id: user.id
            });

            if (error) {
                console.error('get_topic_progress RPC error:', error);
                return {};
            }

            // Convert array to record keyed by subTopicId
            const progressMap: Record<string, SubTopicProgress> = {};
            (data || []).forEach((item: any) => {
                progressMap[item.sub_topic_id] = {
                    subTopicId: item.sub_topic_id,
                    totalQuestions: item.total_questions,
                    attemptedQuestions: item.attempted_questions,
                    correctQuestions: item.correct_questions,
                    lastActivity: item.last_activity
                };
            });

            return progressMap;
        } catch (error) {
            console.error('getTopicProgress error:', error);
            return {};
        }
    };

    // --- Session Persistence Methods ---

    const saveSectionProgress = async (
        sectionId: string,
        data: any,
        stats: { completed: number; total: number; score: number } = { completed: 0, total: 0, score: 0 },
        entityType: 'course' | 'unit' | 'section' | 'algorithmic' = 'section',
        skipStatusUpdate: boolean = false
    ): Promise<boolean> => {
        if (!isAuthenticated || !user?.id) {
            console.warn('⚠️ Cannot save progress: User not authenticated');
            return false;
        }

        try {
            // Because the backend RPC blindly overwrites status with p_status (defaulting to 'in_progress' if null/undefined),
            // we MUST explicitly send the existing status when skipStatusUpdate is true, otherwise it downgrades to in_progress!
            const existingCache = sectionProgressMap[sectionId];
            const safeFallbackStatus = skipStatusUpdate ? (existingCache?.status || 'in_progress') : 'in_progress';

            const payload: any = {
                p_section_id: sectionId,
                p_user_id: user.id,
                p_data: data,
                p_completed_items: stats.completed,
                p_total_items: stats.total,
                p_status: skipStatusUpdate ? safeFallbackStatus : 'in_progress',
                p_score: stats.score,
                p_entity_type: entityType
            };

            // console.log('📡 Calling save_section_progress:', { sectionId, stats, skipStatusUpdate });
            const { data: rpcData, error } = await supabase.rpc('save_section_progress', payload);

            if (error) {
                console.error('❌ save_section_progress error:', error);
                return false;
            }

            // OPTIMISTIC UPDATE: Update local cache immediately
            setSectionProgressMap(prev => {
                const existing = prev[sectionId];
                const newStatus = skipStatusUpdate ? (existing?.status || 'in_progress') : (existing?.status === 'completed' ? 'completed' : 'in_progress');

                return {
                    ...prev,
                    [sectionId]: {
                        ...existing,
                        section_id: sectionId,
                        status: newStatus,
                        data: data,
                        correct_questions: stats.completed,
                        total_questions: stats.total,
                        score: stats.score,
                        last_accessed_at: new Date().toISOString(),
                        user_id: user.id
                    } as UserSectionProgress
                };
            });

            return true;
        } catch (error) {
            console.error('saveSectionProgress error:', error);
            return false;
        }
    };

    const completeSectionSession = async (sectionId: string, score: number, totalQuestions: number, correctQuestions: number, data: any, entityType: 'course' | 'unit' | 'section' | 'algorithmic' = 'section', skipStatusUpdate: boolean = false): Promise<boolean> => {
        if (!isAuthenticated || !user?.id) {
            console.warn('⚠️ Cannot complete session: User not authenticated');
            return false;
        }

        try {
            // Use save_section_progress but force status to completed (unless skipped)
            const payload: any = {
                p_section_id: sectionId,
                p_user_id: user.id,
                p_data: data,
                p_completed_items: correctQuestions,
                p_total_items: totalQuestions,
                p_status: skipStatusUpdate ? undefined : 'completed',
                p_score: score,
                p_entity_type: entityType
            };

            const { data: rpcData, error } = await supabase.rpc('save_section_progress', payload);

            if (error) {
                console.error('❌ complete_section_session (via save) error:', error);
                return false;
            }

            // OPTIMISTIC UPDATE: Update local status
            setSectionProgressMap(prev => {
                const existing = prev[sectionId];
                const newStatus = skipStatusUpdate ? (existing?.status || 'in_progress') : 'completed';

                return {
                    ...prev,
                    [sectionId]: {
                        ...existing,
                        section_id: sectionId,
                        status: newStatus,
                        score: score,
                        correct_questions: correctQuestions,
                        total_questions: totalQuestions,
                        data: data,
                        last_accessed_at: new Date().toISOString(),
                        user_id: user.id
                    } as UserSectionProgress
                };
            });

            if (activities.length === 0) {
                setActivities(INITIAL_ACTIVITIES);
            }

            return true;
        } catch (error) {
            console.error('completeSectionSession error:', error);
            return false;
        }
    };

    // --- Aggregation Methods ---

    const getUnitProgress = async (unitId: string) => {
        if (!user?.id) return null;
        const { data, error } = await supabase.rpc('get_unit_progress_stats', { p_user_id: user.id, p_topic_id: unitId });
        if (error) {
            console.error('getUnitProgress error:', error);
            return null;
        }
        return data;
    };

    const getCourseProgress = async (courseScope: string) => {
        if (!user?.id) return null;

        // Map frontend courseScope ('AB', 'BC') to database course_scope ('ab_only', 'bc_only')
        let dbScope = courseScope;
        if (courseScope === 'AB') dbScope = 'ab_only';
        if (courseScope === 'BC') dbScope = 'bc_only';

        const { data, error } = await supabase.rpc('get_course_progress_stats', { p_user_id: user.id, p_course_scope: dbScope });
        if (error) {
            console.error('getCourseProgress error:', error);
            return null;
        }
        return data;
    };

    const diagSupabase = async () => {
        console.log('🔍 Running Supabase Diagnostics...');
        try {
            const { data, error } = await supabase.rpc('diagnose_supabase_connection');
            if (error) {
                console.error('❌ Diagnostic Error:', error);
                alert(`Supabase Connection Problem: ${error.message}`);
            } else {
                console.log('✅ Diagnostic Data:', data);
                alert(`Supabase Connection OK! User: ${data.authenticated_user || 'Guest'}`);
            }
        } catch (err: any) {
            console.error('❌ Diagnostic Exception:', err);
            alert(`Diagnostic Failed: ${err.message}`);
        }
    };

    const fetchFriends = async () => {
        if (!user?.id) return;
        try {
            const { data, error } = await supabase
                .from('friend_requests')
                .select('sender_id, receiver_id')
                .eq('status', 'accepted')
                .or(`sender_id.eq.${user.id},receiver_id.eq.${user.id}`);

            if (error) throw error;

            const ids = new Set<string>(data.map((req: any) =>
                req.sender_id === user.id ? req.receiver_id : req.sender_id
            ));
            setFriendIds(ids);
            console.log('✅ Friends loaded:', ids.size);
        } catch (err) {
            console.error('Error fetching friends:', err);
        }
    };

    const resetSectionProgress = async (sectionId: string): Promise<boolean> => {
        try {
            const { error } = await supabase
                .from('user_section_progress')
                .delete()
                .eq('user_id', user.id)
                .eq('section_id', sectionId);

            if (error) throw error;

            setSectionProgressMap(prev => {
                const next = { ...prev };
                delete next[sectionId];
                return next;
            });
            return true;
        } catch (error) {
            console.error('resetSectionProgress error:', error);
            return false;
        }
    };

    // Soft reset: preserves summaryHistory while clearing current session data
    const softResetSectionProgress = async (sectionId: string): Promise<boolean> => {
        try {
            // Read existing data to preserve summaryHistory
            const existing = sectionProgressMap[sectionId];
            const existingData = existing?.data || {};
            const preservedHistory = existingData.summaryHistory || [];

            // Build reset data — only keep summaryHistory
            const resetData = {
                summaryHistory: preservedHistory,
                firstAttempt: { status: 'not_started' as const },
                userAnswers: {},
                questionResults: {},
                currentIncorrectIds: [],
                markedQuestionIds: []
            };

            const payload: any = {
                p_section_id: sectionId,
                p_user_id: user.id,
                p_data: resetData,
                p_completed_items: 0,
                p_total_items: 0,
                p_status: 'in_progress',
                p_score: 0,
                p_entity_type: existing?.entity_type || 'section'
            };

            const { error } = await supabase.rpc('save_section_progress', payload);
            if (error) throw error;

            // Optimistic update
            setSectionProgressMap(prev => ({
                ...prev,
                [sectionId]: {
                    ...prev[sectionId],
                    section_id: sectionId,
                    status: 'in_progress',
                    data: resetData,
                    correct_questions: 0,
                    total_questions: 0,
                    score: 0,
                    last_accessed_at: new Date().toISOString(),
                    user_id: user.id
                } as UserSectionProgress
            }));

            console.log(`🔄 [softReset] Preserved ${preservedHistory.length} history entries for ${sectionId}`);
            return true;
        } catch (error) {
            console.error('softResetSectionProgress error:', error);
            return false;
        }
    };

    const logUserActivity = async (params: {
        sectionId: string;
        attemptType: 'first_attempt' | 'review';
        score: number;
        correctCount: number;
        totalQuestions: number;
        data: any;
    }): Promise<string | null> => {
        if (!isAuthenticated || !user?.id) return null;

        try {
            const { data, error } = await supabase.rpc('log_user_activity', {
                p_section_id: params.sectionId,
                p_attempt_type: params.attemptType,
                p_score: params.score,
                p_correct_count: params.correctCount,
                p_total_questions: params.totalQuestions,
                p_data: params.data
            });

            if (error) {
                console.error('Error logging activity:', error);
                return null;
            }

            return data; // returns the UUID of the activity
        } catch (error) {
            console.error('logUserActivity exception:', error);
            return null;
        }
    };

    const getUserActivities = async (sectionId: string): Promise<any[]> => {
        if (!isAuthenticated || !user?.id) return [];

        try {
            const { data, error } = await supabase.rpc('get_user_activities', {
                p_section_id: sectionId
            });

            if (error) {
                console.error('Error fetching activities:', error);
                return [];
            }

            return data || [];
        } catch (error) {
            console.error('getUserActivities exception:', error);
            return [];
        }
    };

    const getSectionProgress = async (sectionId: string): Promise<UserSectionProgress | null> => {
        try {
            const { data, error } = await supabase.rpc('get_section_progress', {
                p_section_id: sectionId
            });

            if (error) throw error;
            if (data && data.length > 0) {
                return data[0] as UserSectionProgress;
            }
            return null;
        } catch (error) {
            console.error('Error fetching section progress:', error);
            return null;
        }
    };

    // --- Bulk Fetch Logic for Caching ---
    const fetchIncorrectQuestions = async () => {
        if (!user?.id) return;
        try {
            // Get questions where the LATEST attempt is incorrect
            const { data: qData, error: qError } = await supabase
                .from('question_attempts')
                .select('question_id, is_correct, created_at')
                .eq('user_id', user.id)
                .order('created_at', { ascending: false });

            if (qError) throw qError;

            const currentIncorrect = new Set<string>();
            const seen = new Set<string>();
            (qData as any[])?.forEach(attempt => {
                if (!seen.has(attempt.question_id)) {
                    seen.add(attempt.question_id);
                    if (!attempt.is_correct) {
                        currentIncorrect.add(attempt.question_id);
                    }
                }
            });
            setIncorrectQuestionIds(currentIncorrect);
        } catch (err) {
            console.error('Error fetching incorrect questions in AppContext:', err);
        }
    };

    const fetchWrongAnswersCache = async () => {
        if (!user?.id) return;
        try {
            const { data: wrongAttempts, error: attemptsError } = await supabase
                .from('question_attempts')
                .select('question_id, is_correct, created_at, selected_option_id')
                .eq('user_id', user.id)
                .eq('is_correct', false)
                .order('created_at', { ascending: false });
            if (attemptsError) throw attemptsError;

            // Load dismissed map to filter client-side initially
            let dismissedMap: Record<string, string> = {};
            try {
                const saved = localStorage.getItem(`wrong_answer_dismissed_map_${user.id}`);
                if (saved) dismissedMap = JSON.parse(saved);
            } catch { }

            const uniqueQuestionIds = new Set<string>();
            const wrongAnswerMap: Record<string, any> = {};
            for (const attempt of (wrongAttempts || [])) {
                const qid = attempt.question_id;
                const dismissedAt = dismissedMap[qid];
                if (dismissedAt && new Date(attempt.created_at).getTime() <= new Date(dismissedAt).getTime()) {
                    continue; // Skipped dismissed
                }

                if (!uniqueQuestionIds.has(qid)) {
                    uniqueQuestionIds.add(qid);
                    wrongAnswerMap[qid] = {
                        questionId: qid, wrongCount: 1, lastWrongAt: attempt.created_at,
                        firstWrongAt: attempt.created_at, lastSelectedOptionId: attempt.selected_option_id,
                    };
                } else {
                    wrongAnswerMap[qid].wrongCount++;
                    wrongAnswerMap[qid].firstWrongAt = attempt.created_at;
                }
            }
            const questionIds = Array.from(uniqueQuestionIds);
            if (questionIds.length === 0) {
                setWrongAnswersCache({ items: [], lastFetched: Date.now() });
                return;
            }

            const { data: questionDetails, error: qError } = await supabase
                .from('questions')
                .select('id, title, topic, sub_topic_id, type, difficulty, prompt, prompt_type, options, correct_option_id, explanation, calculator_allowed, latex')
                .in('id', questionIds);

            if (qError) throw qError;

            const detailsMap: Record<string, any> = {};
            (questionDetails || []).forEach((q: any) => { detailsMap[q.id] = q; });

            const result = questionIds
                .map(qid => ({ ...wrongAnswerMap[qid], question: detailsMap[qid] || null }))
                .filter(w => w.question !== null)
                .sort((a, b) => new Date(b.lastWrongAt).getTime() - new Date(a.lastWrongAt).getTime());

            setWrongAnswersCache({ items: result, lastFetched: Date.now() });
        } catch (err) {
            console.error('Failed to pre-fetch wrong answers cache:', err);
        }
    };

    const fetchAllUserProgress = async () => {
        if (!user?.id) return;
        try {
            // Parallelize for performance
            await Promise.all([
                (async () => {
                    const { data, error } = await supabase.rpc('get_all_user_progress');
                    if (error) {
                        console.error('Error fetching all user progress:', error);
                        return;
                    }
                    if (data) {
                        const map: Record<string, UserSectionProgress> = {};
                        data.forEach((p: any) => {
                            map[p.section_id] = p;
                        });
                        setSectionProgressMap(map);
                        localStorage.setItem('section_progress_cache', JSON.stringify(map));
                    }
                })(),
                fetchIncorrectQuestions(),
                fetchRadarData(),
                fetchLineData(),
                fetchBadgeStatus(),
                getUserInsights(),
                fetchUserPrestige()
            ]);

            // Trigger UI to fetch any newly unlocked achievements
            window.dispatchEvent(new Event('refreshAchievements'));

        } catch (error) {
            console.error('fetchAllUserProgress error:', error);
        }
    };

    // Load public data on mount
    useEffect(() => {
        fetchSections();
        fetchContent();
        fetchSkills();
        fetchQuestions();
    }, []);

    // Load user-specific data when user authenticates
    useEffect(() => {
        if (user.id && isAuthenticated) {
            const loadData = async () => {
                // 1. Generate/Update Automated System Notifications (Atomic DB check)
                await supabase.rpc('generate_system_notifications', { p_user_id: user.id });

                // 2. Load all user data
                await Promise.all([
                    fetchAllUserProgress(),
                    fetchRadarData(),
                    fetchLineData(),
                    fetchRecentActivities(),
                    fetchBadgeStatus(),
                    getUserInsights(),
                    fetchFriends(),
                    fetchUserPoints(),
                    fetchQuestions(),
                    fetchWrongAnswersCache()
                ]);
            };
            loadData();
        } else {
            setSectionProgressMap({});
            setFriendIds(new Set());
        }
    }, [user.id, isAuthenticated]);

    // Badge Polling Timer: refresh badges every 5 minutes
    useEffect(() => {
        if (!user.id || !isAuthenticated) return;

        const intervalId = setInterval(() => {
            fetchBadgeStatus();
        }, 5 * 60 * 1000); // 5 minutes

        return () => clearInterval(intervalId);
    }, [user.id, isAuthenticated]);

    const getSectionStatus = (sectionId: string): 'not_started' | 'in_progress' | 'completed' => {
        // Direct Lookup
        if (sectionProgressMap[sectionId]) return sectionProgressMap[sectionId].status;

        // COMPAT: Fallback Lookup for Unified IDs (ABBC_ -> AB_/BC_ or vice versa)
        const base = sectionId.replace(/^(ABBC_|AB_|BC_|Both_)/, '');

        // 1. If we have ABBC_Limits, check if we have AB_Limits status stored
        const legacyAB = `AB_${base}`;
        if (sectionProgressMap[legacyAB]) return sectionProgressMap[legacyAB].status;

        const legacyBC = `BC_${base}`;
        if (sectionProgressMap[legacyBC]) return sectionProgressMap[legacyBC].status;

        // 2. If we asked for AB_Limits, check if ABBC_Limits exists
        const unified = `ABBC_${base}`;
        if (sectionProgressMap[unified]) return sectionProgressMap[unified].status;

        // 3. Finally try the raw base itself
        if (sectionProgressMap[base]) return sectionProgressMap[base].status;

        return 'not_started';
    };

    const getSectionProgressData = (sectionId: string): UserSectionProgress | undefined => {
        if (!sectionId) return undefined;
        if (sectionProgressMap[sectionId]) return sectionProgressMap[sectionId];

        // Try normalized IDs
        const base = sectionId.replace(/^(ABBC_|AB_|BC_|Both_)/, '');

        // 1. If we have ABBC_Limits, check if we have AB_Limits status stored
        const legacyAB = `AB_${base}`;
        if (sectionProgressMap[legacyAB]) return sectionProgressMap[legacyAB];

        const legacyBC = `BC_${base}`;
        if (sectionProgressMap[legacyBC]) return sectionProgressMap[legacyBC];

        // 2. If we asked for AB_Limits, check if ABBC_Limits exists
        const unified = `ABBC_${base}`;
        if (sectionProgressMap[unified]) return sectionProgressMap[unified];

        // 2. Finally try the raw base itself
        if (sectionProgressMap[base]) return sectionProgressMap[base];

        return undefined;
    };

    const getTopicSectionProgress = async (topicId: string): Promise<UserSectionProgress[]> => {
        try {
            const { data, error } = await supabase.rpc('get_topic_section_progress', {
                p_topic_id: topicId
            });

            if (error) throw error;
            return (data || []) as UserSectionProgress[];
        } catch (error) {
            console.error('Error fetching topic section progress:', error);
            return [];
        }
    };



    // Calculate Course Mastery dynamically from Radar Data
    const getCourseMastery = (courseType: CourseType) => {
        // Define which topics belong to which course
        // AB uses Units 1-8. BC uses Units 1-10.
        const abTopics = ['Unit 1', 'Unit 2', 'Unit 3', 'Unit 4', 'Unit 5', 'Unit 6', 'Unit 7', 'Unit 8'];
        const bcTopics = [...abTopics, 'Unit 9', 'Unit 10'];

        const relevantTopics = courseType === 'AB' ? abTopics : bcTopics;

        const totalScore = radarData.reduce((acc, item) => {
            if (relevantTopics.includes(item.subject)) {
                return acc + item.A;
            }
            return acc;
        }, 0);

        // Return average
        return Math.round(totalScore / relevantTopics.length);
    };

    // Effect: Check for existing Supabase session on app start (persistent login)
    useEffect(() => {
        const restoreSession = async () => {
            // Check if we have a cached user — if so, render immediately from cache
            const hasCachedUser = typeof window !== 'undefined' && localStorage.getItem('user_profile_cache') !== null;

            // If we have cache, stop loading immediately so the UI renders from cache
            // The full session restore will update in the background
            if (hasCachedUser) {
                setIsAuthLoading(false);
            }

            // Safety valve: Force stop loading after 3 seconds to prevent white screen
            const safetyTimeout = setTimeout(() => {
                console.warn('⚠️ Session restore timed out, forcing load completion.');
                setIsAuthLoading(false);
            }, 3000);

            try {
                const { data: { session } } = await supabase.auth.getSession();
                if (session?.user) {
                    const email = session.user.email || '';

                    // Run profile, stats, and titles fetches in parallel
                    const [profileResult, statsResult, titlesResult] = await Promise.all([
                        supabase
                            .from('user_profiles')
                            .select('is_creator, name, avatar_url, subscription_tier, subscription_period_end, has_seen_pro_intro, bio, avatar_color, show_name, show_email, show_bio, streak_days, equipped_title_id, equipped_title:titles(*), locked_practice_mode, practice_mode_locked_at, has_claimed_welcome_gift')
                            .eq('id', session.user.id)
                            .single(),
                        supabase.rpc('get_user_stats', { target_user_id: session.user.id }),
                        supabase
                            .from('titles')
                            .select('*')
                            .order('threshold', { ascending: true })
                    ]);

                    const profile = profileResult.data;
                    const statsData = statsResult.data;
                    const allTitles = titlesResult.data;

                    if (allTitles) setAvailableTitles(allTitles);

                    // 优先使用user_profiles中的name（用户可能在Settings里更新过）
                    const rawName = profile?.name || session.user.user_metadata?.name || email.split('@')[0];
                    const name = rawName.charAt(0).toUpperCase() + rawName.slice(1);

                    // ✅ Authorization Logic Source of Truth:
                    // Database Table: public.user_profiles
                    // Column: is_creator (boolean)
                    // Hardcoded Overrides: newmao6120@gmail.com OR *@newmaos.com
                    const isSuperAdmin = email === 'newmao6120@gmail.com';
                    const isAuthorizedDomain = email.toLowerCase().endsWith('@newmaos.com');

                    // 优先使用user_profiles中的avatar_url
                    const dbAvatarUrl = profile?.avatar_url;
                    const finalAvatarUrl = dbAvatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=f9d406&color=1c1a0d&bold=true`;

                    setIsAuthenticated(true);
                    setUser(prev => {
                        const updatedUser = {
                            ...prev,
                            id: session.user.id,
                            name: name,
                            email: email,
                            avatarUrl: finalAvatarUrl,
                            isCreator: isSuperAdmin || isAuthorizedDomain || profile?.is_creator || false,
                            subscriptionTier: profile?.subscription_tier || 'basic',
                            subscriptionPeriodEnd: profile?.subscription_period_end,
                            hasSeenProIntro: profile?.has_seen_pro_intro || false,
                            hasClaimedWelcomeGift: profile?.has_claimed_welcome_gift || false,
                            equippedTitle: profile?.equipped_title ? (() => {
                                const t = Array.isArray(profile.equipped_title) ? profile.equipped_title[0] : profile.equipped_title;
                                return {
                                    id: profile.equipped_title_id || '',
                                    name: t.name,
                                    category: t.category,
                                    description: t.description || '',
                                    threshold: t.threshold || 0
                                };
                            })() : undefined,
                            stats: statsData || { posts: 0, friends: 0, channels: 0 },
                            bio: profile?.bio || '',
                            avatarColor: profile?.avatar_color || 'linear-gradient(135deg, #FFD700 0%, #FF8C00 100%)',
                            showName: profile?.show_name ?? true,
                            showEmail: profile?.show_email ?? false,
                            showBio: profile?.show_bio ?? true,
                            streakDays: profile?.streak_days || 0,
                            equippedTitleId: profile?.equipped_title_id,
                            preferences: {
                                emailNotifications: (profile as any)?.email_notifications ?? true,
                                soundEffects: (profile as any)?.sound_effects ?? true
                            },
                            createdAt: session.user.created_at,
                            lockedPracticeMode: (profile as any)?.locked_practice_mode || null,
                            practiceModLockedAt: (profile as any)?.practice_mode_locked_at || null
                        };
                        localStorage.setItem('user_profile_cache', JSON.stringify(updatedUser));
                        return updatedUser;
                    });

                    // ONLY Sync to DB if the database has NO avatar_url (initial setup)
                    if (!dbAvatarUrl) {
                        console.log('[Auth] Profile has no avatar. Syncing default UI Avatar to DB.');
                        supabase.from('user_profiles')
                            .upsert({
                                id: session.user.id,
                                avatar_url: finalAvatarUrl,
                                name: name // Ensure name is also synced if profile is new
                            })
                            .then(({ error }) => {
                                if (error) console.error('Failed to sync default profile to user_profiles:', error);
                            });
                    }
                    console.log('✅ Session restored for:', email);
                } else {
                    // Critical Fix: Clear any stale local auth state if backend session is gone
                    setIsAuthenticated(false);
                    setUser(INITIAL_USER);
                    setIsCreatorAuthenticated(false);
                    localStorage.removeItem('user_profile_cache');
                }
            } catch (error) {
                console.log('No existing session found or restoration failed', error);
                setIsAuthenticated(false);
                setUser(INITIAL_USER);
                localStorage.removeItem('user_profile_cache');
            } finally {
                clearTimeout(safetyTimeout);
                setIsAuthLoading(false); // Stop loading regardless of result
            }
        };

        restoreSession();

        // Listen for auth state changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            if (event === 'SIGNED_OUT') {
                setIsAuthenticated(false);
                setIsCreatorAuthenticated(false);
                setUser(INITIAL_USER);
                setSectionProgressMap({});
                setFriendIds(new Set());
                // Full storage clear to ensure clean slate
                localStorage.clear();
                sessionStorage.clear();
                sessionStorage.setItem('hasDismissedLoginPrompt', 'true');
            } else if (event === 'SIGNED_IN' && session) {
                // Always restore full profile from DB (avatar, titles, etc.)
                restoreSession();
            } else if (event === 'TOKEN_REFRESHED' && session) {
                // Keep auth_token in sync with refreshed token
                localStorage.setItem('auth_token', session.access_token);
            }
        });

        return () => subscription.unsubscribe();
    }, []);

    // =====================================================
    // ENTERPRISE UNIT RECOMMENDATION ENGINE v2.0
    // Multi-factor scoring with weighted composite formula
    // Factors: MasteryGap, RecencyDecay, CoverageGap, SequentialBonus, WeakBoost
    // =====================================================
    useEffect(() => {
        if (!user?.id) return;

        const topics = COURSE_TOPICS[user.currentCourse];
        if (!topics || topics.length === 0) return;

        // ── Phase 1: Build per-unit feature vectors ──────────────────
        const unitFeatures = topics.map((topic, index) => {
            const unitLabel = topic.subject.split(':')[0].trim();
            const radar = radarData.find(r => r.subject === unitLabel);
            const mastery = radar?.A ?? 0;
            const topicName = topic.id.includes('_')
                ? topic.id.split('_').slice(1).join('_')
                : topic.id;

            // ─ Factor 1: MasteryGap ─
            // Sigmoid-compressed gap: emphasizes mid-range gaps (30-70%) over extremes
            // Raw gap: (100 - mastery) / 100
            // Compressed: 2 / (1 + exp(-5 * (rawGap - 0.5))) - 1, then clipped [0,1]
            const rawGap = (100 - mastery) / 100;
            const compressedGap = 2 / (1 + Math.exp(-5 * (rawGap - 0.5))) - 1;
            const masteryGap = Math.max(0, Math.min(1, (rawGap + compressedGap) / 2));

            // ─ Factor 2: RecencyDecay ─
            // How long since user last practiced THIS unit?
            // Sources: sectionProgressMap entries matching this topic
            // Bug6 fix: robust multi-format topic matching
            const matchingProgress = Object.values(sectionProgressMap || {}).filter(
                (p: any) => {
                    const st = (p.data?.sessionTopic || '').toLowerCase();
                    const sid = (p.section_id || '').toLowerCase();
                    const tn = topicName.toLowerCase();
                    const tid = topic.id.toLowerCase();
                    // Match by sessionTopic, section_id, or topic_id substring
                    return st === tn || st === tid || st.includes(tn) || tn.includes(st)
                        || sid.includes(tn) || sid.includes(tid);
                }
            );
            const lastAccessedMs = matchingProgress.length > 0
                ? Math.max(...matchingProgress.map((s: any) =>
                    new Date(s.last_accessed_at || 0).getTime()))
                : 0;
            const daysSinceLast = lastAccessedMs > 0
                ? (Date.now() - lastAccessedMs) / (1000 * 60 * 60 * 24)
                : -1; // -1 = never practiced
            // Exponential decay: decayScore(d) = 1 - exp(-d / τ), τ = 7 days
            // Peaks at 1.0 when very stale, 0 when just practiced
            const tau = 7;
            const recencyDecay = daysSinceLast < 0
                ? 0.3 // never practiced — moderate, let other factors drive
                : Math.min(1, 1 - Math.exp(-daysSinceLast / tau));

            // ─ Factor 3: CoverageGap ─
            // How many sections/subTopics in this unit has the user NOT attempted?
            const unitContent = topicContent[topic.id]
                || Object.values(topicContent).find(u => u.id === topic.id || u.id.includes(topicName));
            const totalSubTopics = unitContent?.subTopics?.length || 1;
            const completedSections = matchingProgress.filter(
                (s: any) => s.status === 'completed' || s.correct_questions > 0
            ).length;
            const coverageGap = Math.max(0, 1 - completedSections / totalSubTopics);

            // ─ Factor 4: SequentialBonus ─
            // Curriculum-order bonus: earlier units get priority IF the user hasn't started them
            // AND the previous unit(s) have high mastery (prerequisites met)
            let sequentialBonus = 0;
            if (mastery === 0 && index > 0) {
                // Bug5 fix: lowered threshold from 40% to 1% — any attempt at prev unit triggers
                const prevUnitLabel = topics[index - 1].subject.split(':')[0].trim();
                const prevRadar = radarData.find(r => r.subject === prevUnitLabel);
                const prevMastery = prevRadar?.A ?? 0;
                if (prevMastery >= 1) {
                    // Previous unit attempted → this is the natural next step
                    sequentialBonus = 0.6 * (1 - index / topics.length);
                }
            } else if (mastery === 0 && index === 0) {
                // First unit, never started — strong sequential signal
                sequentialBonus = 0.8;
            }

            // ─ Factor 5: Started-But-Weak Boost ─
            // Units the user has already invested time in but are below 50% get a boost
            // Rational: reinforce before expanding
            const hasStarted = mastery > 0 || matchingProgress.length > 0;
            const weakBoost = hasStarted && mastery < 50 ? 0.3 * (1 - mastery / 50) : 0;

            // ── Phase 2: Composite Score ─────────────────────────────
            // Mode-dependent weights (Bug1 fix: different modes emphasize different factors)
            const W = getModeWeights(recommendation.mode);

            // ─ Penalty: Strict Prerequisite Blocker ─
            // If the user hasn't meaningfully started the previous unit, strongly penalize jumping ahead.
            let preReqPenalty = 1.0;
            if (index > 0) {
                const prevUnitLabel = topics[index - 1].subject.split(':')[0].trim();
                const prevMastery = radarData.find(r => r.subject === prevUnitLabel)?.A ?? 0;
                if (prevMastery < 40) {
                    preReqPenalty = 0.1; // 90% penalty if previous unit is not solidly started
                }
            }

            const rawWeightedScore =
                W.masteryGap * masteryGap
                + W.recencyDecay * recencyDecay
                + W.coverageGap * coverageGap
                + W.sequentialBonus * sequentialBonus
                + W.weakBoost * weakBoost;

            const weightedScore = rawWeightedScore * preReqPenalty;

            return {
                topic, topicName, unitLabel, mastery, hasStarted,
                // Individual factors
                masteryGap, recencyDecay, coverageGap, sequentialBonus, weakBoost,
                // Composite
                weightedScore,
                daysSinceLast,
                completedSections, totalSubTopics,
                matchingProgressCount: matchingProgress.length,
                hasPenalty: preReqPenalty < 1.0,
            };
        });

        // ── Phase 3: Data sufficiency check ──────────────────────────
        const totalSessions = unitFeatures.reduce((s, u) => s + u.matchingProgressCount, 0);
        const hasAnyProgress = unitFeatures.some(u => u.mastery > 0 || u.hasStarted);

        if (!hasAnyProgress) {
            setRecommendation(prev => ({
                ...prev,
                topic: 'Limits',
                reason: '',
                currentMastery: 0,
                targetMastery: 80,
                mode: 'Adaptive',
                hasData: false,
                weightedScore: 0,
                confidenceLevel: 'low',
                scoringFactors: undefined,
            }));
            return;
        }

        // Confidence level based on amount of data
        const unitsWithData = unitFeatures.filter(u => u.mastery > 0).length;
        const confidenceLevel: 'low' | 'medium' | 'high' =
            totalSessions >= 10 && unitsWithData >= 3 ? 'high'
                : totalSessions >= 3 || unitsWithData >= 2 ? 'medium'
                    : 'low';

        // ── Phase 4: Rank and select ─────────────────────────────────
        const sorted = [...unitFeatures].sort((a, b) => b.weightedScore - a.weightedScore);
        const best = sorted[0];

        // ── Phase 5: Mode selection (based on mastery level) ─────────
        let mode: SessionMode;
        // Override with locked mode if applicable
        if (user.lockedPracticeMode && user.practiceModLockedAt) {
            const lockedAt = new Date(user.practiceModLockedAt);
            const sixMonthsLater = new Date(lockedAt);
            sixMonthsLater.setMonth(sixMonthsLater.getMonth() + 6);
            if (new Date() < sixMonthsLater) {
                mode = user.lockedPracticeMode;
            } else {
                mode = best.mastery >= 70 ? 'Review' : 'Adaptive';
            }
        } else {
            mode = best.mastery >= 70 ? 'Review' : 'Adaptive';
        }

        // ── Phase 6: Explainable reason generation ──────────────────
        // Identify the dominant factor(s) that drove this recommendation
        const factors = [
            { name: 'mastery gap', val: best.masteryGap, w: 0.30 },
            { name: 'recency', val: best.recencyDecay, w: 0.20 },
            { name: 'coverage', val: best.coverageGap, w: 0.15 },
            { name: 'sequence', val: best.sequentialBonus, w: 0.15 },
            { name: 'reinforcement', val: best.weakBoost, w: 0.20 },
        ].filter(f => f.val > 0.05).sort((a, b) => b.val * b.w - a.val * a.w);

        let reason: string;
        const topFactor = factors[0]?.name || 'mastery gap';
        const score = Math.round(best.weightedScore * 100);

        if (best.hasPenalty) {
            reason = `${best.unitLabel} is locked behind prerequisites. Please complete earlier units first. [Score: ${score}]`;
        } else if (!best.hasStarted) {
            if (best.sequentialBonus > 0.3) {
                reason = `${best.unitLabel} is your next logical step — prerequisite units look solid. [Score: ${score}]`;
            } else {
                reason = `${best.unitLabel} hasn't been started. Expanding coverage will strengthen your foundation. [Score: ${score}]`;
            }
        } else if (best.mastery >= 70) {
            const days = best.daysSinceLast > 0 ? `${Math.round(best.daysSinceLast)}d ago` : 'recently';
            reason = `${best.unitLabel} at ${best.mastery}% — revisit to maintain mastery (last practiced ${days}). [Score: ${score}]`;
        } else if (topFactor === 'recency' && best.daysSinceLast > 3) {
            reason = `${best.unitLabel} needs review — ${Math.round(best.daysSinceLast)}d since last practice, risk of decay. [Score: ${score}]`;
        } else if (topFactor === 'reinforcement') {
            reason = `${best.unitLabel} at ${best.mastery}% — reinforce before expanding to new units. [Score: ${score}]`;
        } else if (topFactor === 'coverage') {
            reason = `${best.unitLabel} — only ${best.completedSections}/${best.totalSubTopics} sections covered. Fill gaps. [Score: ${score}]`;
        } else {
            reason = `${best.unitLabel} at ${best.mastery}% — highest improvement potential across all factors. [Score: ${score}]`;
        }

        // ── Phase 7: Commit recommendation ──────────────────────────
        setRecommendation(prev => ({
            ...prev,
            topic: best.topicName,
            reason,
            currentMastery: best.mastery,
            targetMastery: 80,
            mode,
            hasData: true,
            weightedScore: Math.round(best.weightedScore * 1000) / 1000,
            confidenceLevel,
            scoringFactors: {
                masteryGap: Math.round(best.masteryGap * 1000) / 1000,
                recencyDecay: Math.round(best.recencyDecay * 1000) / 1000,
                coverageGap: Math.round(best.coverageGap * 1000) / 1000,
                sequentialBonus: Math.round(best.sequentialBonus * 1000) / 1000,
                weakBoost: Math.round(best.weakBoost * 1000) / 1000,
            },
        }));
    }, [user.currentCourse, radarData, sectionProgressMap, topicContent]);

    const login = (
        email: string,
        username?: string,
        id?: string,
        subscriptionTier: 'basic' | 'pro' = 'basic',
        subscriptionPeriodEnd?: string,
        hasSeenProIntro: boolean = false,
        avatarUrl?: string,
        hasClaimedWelcomeGift: boolean = false
    ) => {
        setIsAuthenticated(true);
        // Use username if provided, otherwise derive from email
        const displayName = username || email.split('@')[0];
        const formattedName = displayName.charAt(0).toUpperCase() + displayName.slice(1);

        const isSuperAdmin = email === 'newmao6120@gmail.com';

        setUser(prev => {
            const finalAvatar = avatarUrl || prev.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(formattedName || 'Student')}&background=f9d406&color=1c1a0d&bold=true`;

            const updatedUser = {
                ...prev,
                id: id || prev.id || '',
                name: formattedName || "Student",
                email: email,
                avatarUrl: finalAvatar,
                isCreator: isSuperAdmin || prev.isCreator,
                subscriptionTier: subscriptionTier,
                subscriptionPeriodEnd: subscriptionPeriodEnd,
                hasSeenProIntro: hasSeenProIntro,
                hasClaimedWelcomeGift: hasClaimedWelcomeGift
            };
            localStorage.setItem('user_profile_cache', JSON.stringify(updatedUser));
            return updatedUser;
        });
    };

    const isPro = useMemo(() => {
        if (user.subscriptionTier !== 'pro') return false;
        
        // If there's an expiration date, verify it hasn't passed
        if (user.subscriptionPeriodEnd) {
            const endDate = new Date(user.subscriptionPeriodEnd);
            if (endDate < new Date()) {
                return false;
            }
        }
        
        return true;
    }, [user.subscriptionTier, user.subscriptionPeriodEnd]);

    const claimFreePro = async () => {
        if (!isAuthenticated || !user.id) return false;

        try {
            const periodEnd = new Date();
            periodEnd.setMonth(periodEnd.getMonth() + 1);
            const periodEndStr = periodEnd.toISOString();

            const { error } = await supabase
                .from('user_profiles')
                .upsert({
                    id: user.id,
                    subscription_tier: 'pro',
                    subscription_period_end: periodEndStr
                });

            if (error) throw error;

            setUser(prev => {
                const updated: User = {
                    ...prev,
                    subscriptionTier: 'pro' as const,
                    subscriptionPeriodEnd: periodEndStr
                };
                localStorage.setItem('user_profile_cache', JSON.stringify(updated));
                return updated;
            });

            // Clear settings red dot immediately
            setNavRedDots(prev => ({ ...prev, settings: false, subscription: false }));

            return true;
        } catch (error) {
            console.error('Failed to claim free pro:', error);
            return false;
        }
    };

    // =====================================================
    // POINTS ECONOMY FUNCTIONS
    // =====================================================

    const fetchUserPoints = async () => {
        if (!user.id) return;
        try {
            const { data, error } = await supabase.rpc('get_points_summary', { p_user_id: user.id });
            if (error) {
                console.error('fetchUserPoints error:', error);
                return;
            }
            if (data) {
                const pointsData = {
                    balance: data.balance || 0,
                    lifetimeEarned: data.lifetime_earned || 0
                };
                setUserPoints(pointsData);
                localStorage.setItem('user_points_cache', JSON.stringify(pointsData));
            }
        } catch (err) {
            console.error('fetchUserPoints error:', err);
        }
    };

    const triggerCoinAnimation = (amount: number, x?: number, y?: number, mode: 'earn' | 'spend' = 'earn') => {
        // Default to center of screen if no coordinates provided
        const posX = x ?? window.innerWidth / 2;
        const posY = y ?? window.innerHeight / 2;

        // Dispatch custom event that CoinCollector component will listen to
        const event = new CustomEvent('coin-collect', {
            detail: { amount, x: posX, y: posY, mode }
        });
        window.dispatchEvent(event);
    };

    // Pre-unlock AudioContext during user gesture (before async work)
    // Browsers block AudioContext creation outside of user interaction
    const unlockAudioContext = () => {
        try {
            const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
            // Resume if suspended (required by Chrome autoplay policy)
            if (ctx.state === 'suspended') ctx.resume();
            // Dispatch a silent unlock event that CoinCollector can pick up
            window.dispatchEvent(new CustomEvent('audio-unlock'));
        } catch { }
    };

    const awardPoints = async (
        amount: number,
        type: string,
        sourceId?: string,
        description?: string,
        idempotencyKey?: string,
        x?: number,
        y?: number,
        suppressAnimation?: boolean
    ): Promise<{ success: boolean; newBalance?: number }> => {
        if (!user.id || amount <= 0) return { success: false };
        try {
            // Pre-unlock audio context IMMEDIATELY on user gesture (before async)
            unlockAudioContext();

            const { data, error } = await supabase.rpc('award_points', {
                p_user_id: user.id,
                p_amount: amount,
                p_type: type,
                p_source_id: sourceId || null,
                p_description: description || '',
                p_idempotency_key: idempotencyKey || null
            });
            if (data?.success) {
                // ✅ Trigger animation ONLY on successful award, and if not suppressed
                if (!suppressAnimation) {
                    triggerCoinAnimation(amount, x, y);
                }

                setUserPoints(prev => {
                    const nextData = {
                        balance: data.new_balance,
                        lifetimeEarned: prev.lifetimeEarned + (Number(amount) || 0)
                    };
                    localStorage.setItem('user_points_cache', JSON.stringify(nextData));
                    return nextData;
                });

                setRecentPointsTransaction({ amount, description: description || type });
                // Auto-clear after animation
                setTimeout(() => setRecentPointsTransaction(null), 2000);
                return { success: true, newBalance: data.new_balance };
            }
            return { success: false };
        } catch (err) {
            console.error('awardPoints error:', err);
            return { success: false };
        }
    };

    const spendPoints = async (amount: number, type: string): Promise<{ success: boolean; newBalance?: number; reason?: string }> => {
        if (!user.id || amount <= 0) return { success: false, reason: 'invalid_amount' };
        try {
            const { data, error } = await supabase.rpc('spend_points', { p_amount: amount, p_type: type });
            if (error) {
                console.error('spendPoints RPC error:', error);
                return { success: false, reason: error.message };
            }
            if (data?.success) {
                setUserPoints(prev => {
                    const nextData = {
                        ...prev,
                        balance: data.new_balance
                    };
                    localStorage.setItem('user_points_cache', JSON.stringify(nextData));
                    return nextData;
                });
                return { success: true, newBalance: data.new_balance };
            }
            return { success: false, reason: data?.reason };
        } catch (err: any) {
            console.error('spendPoints error:', err);
            return { success: false, reason: err.message };
        }
    };

    const fetchGameStats = async (gameId: string) => {
        if (!user.id) return null;
        try {
            const { data, error } = await supabase
                .from('game_stats')
                .select('*')
                .eq('user_id', user.id)
                .eq('game_id', gameId)
                .single();
            if (error && error.code !== 'PGRST116') throw error;
            return data;
        } catch (err) {
            console.error('fetchGameStats error:', err);
            return null;
        }
    };

    const saveGameStats = async (gameId: string, updates: { high_score?: number; coins_earned?: number; stardust_earned?: number }) => {
        if (!user.id) return false;
        try {
            const existing = await fetchGameStats(gameId);
            const { data, error } = await supabase.from('game_stats').upsert({
                user_id: user.id,
                game_id: gameId,
                high_score: updates.high_score !== undefined ? Math.max(updates.high_score, existing?.high_score || 0) : (existing?.high_score || 0),
                total_played: (existing?.total_played || 0) + 1,
                coins_earned: (existing?.coins_earned || 0) + (updates.coins_earned || 0),
                stardust_earned: (existing?.stardust_earned || 0) + (updates.stardust_earned || 0),
                last_played: new Date().toISOString()
            }, { onConflict: 'user_id, game_id' });
            
            if (error) throw error;
            return true;
        } catch (err) {
            console.error('saveGameStats error:', err);
            return false;
        }
    };

    const redeemProWithPoints = async (): Promise<{ success: boolean; reason?: string; newBalance?: number; shortfall?: number }> => {
        if (!user.id) return { success: false, reason: 'not_authenticated' };
        try {
            const { data, error } = await supabase.rpc('redeem_pro_with_points', { p_user_id: user.id });
            if (error) {
                console.error('redeemProWithPoints RPC error:', error);
                return { success: false, reason: error.message };
            }
            if (data?.success) {
                // Update local state
                setUserPoints(prev => ({ ...prev, balance: data.new_balance }));
                setUser(prev => {
                    const updated: User = {
                        ...prev,
                        subscriptionTier: 'pro' as const,
                        subscriptionPeriodEnd: data.period_end
                    };
                    localStorage.setItem('user_profile_cache', JSON.stringify(updated));
                    return updated;
                });
                // Immediately clear settings red dot — badge should disappear once Pro is active
                setNavRedDots(prev => ({ ...prev, settings: false, subscription: false }));
                return { success: true, newBalance: data.new_balance };
            }
            return { success: false, reason: data?.reason, shortfall: data?.shortfall };
        } catch (err: any) {
            console.error('redeemProWithPoints error:', err);
            return { success: false, reason: err.message };
        }
    };

    const fetchUserPrestige = useCallback(async () => {
        if (!user?.id) return;
        try {
            const { data, error } = await supabase
                .from('user_prestige')
                .select('*')
                .eq('user_id', user.id)
                .single();

            if (error && error.code !== 'PGRST116') {
                console.error('Error fetching prestige:', error);
            }

            if (data) {
                setUserPrestige(data);
            } else {
                // Default / Initial State
                setUserPrestige({
                    user_id: user.id,
                    planet_level: 1,
                    star_level: 0,
                    current_stardust: 0,
                    total_stardust_collected: 0
                });
            }
        } catch (err) {
            console.error('Exception fetching prestige:', err);
        }
    }, [user?.id]);

    const purchaseStardust = async (amountCoins: number) => {
        if (!user?.id) return { success: false, message: 'User not found' };
        try {
            console.log('💰 Purchasing stardust:', amountCoins);
            const { data, error } = await supabase.rpc('purchase_stardust', { amount_coins: amountCoins });
            console.log('💰 Purchase result:', { data, error });

            if (error) throw error;

            // Handle RPC returning literal boolean or object
            // The RPC returns jsonb: { success: true, ... }
            if (data && (data.success || data === true)) {
                await fetchUserPoints();
                await fetchUserPrestige();
                return { success: true };
            } else {
                return { success: false, message: data?.message || 'Purchase failed' };
            }
        } catch (err: any) {
            console.error('Purchase stardust failed:', err);
            return { success: false, message: err.message || 'Transaction failed' };
        }
    };

    const injectStardust = async (amount: number) => {
        if (!user?.id) return { success: false, message: 'User not found' };
        try {
            const { data, error } = await supabase.rpc('inject_stardust', { amount_to_inject: amount });
            if (error) throw error;

            if (data.success) {
                await fetchUserPrestige();
                return { success: true };
            } else {
                return { success: false, message: data.message };
            }
        } catch (err: any) {
            console.error('Inject stardust failed:', err);
            return { success: false, message: err.message || 'Injection failed' };
        }
    };

    useEffect(() => {
        if (isAuthenticated) {
            fetchUserPrestige();
        }
    }, [isAuthenticated, fetchUserPrestige]);

    const performDailyCheckin = useCallback(async () => {
        if (!user.id) return { success: false, reason: 'not_authenticated' };
        // Instantly clear red dot for fast UI response
        clearBadgeLocally('dashboard');
        try {
            const localDate = new Date().toLocaleDateString('en-CA');
            const { data, error } = await supabase.rpc('perform_daily_checkin', {
                p_user_id: user.id,
                p_client_date: localDate
            });
            if (error) {
                console.error('performDailyCheckin RPC error:', error);
                return { success: false, reason: error.message };
            }
            if (data?.success) {
                // Refresh points balance
                await fetchUserPoints();

                // Ensure latest badges are fetched so Dashboard red dot disappears
                await fetchBadgeStatus();

                // Trigger animation (default to center)
                triggerCoinAnimation(data.total_points || 20);

                // Mark check-in status as done globally
                setCheckinStatus('checked_in');
                sessionStorage.setItem('streak_checked_today', 'true');

                // Clear any check-in notifications (Deduplication fix)

                return {
                    success: true,
                    streakDay: data.streak_day,
                    basePoints: data.base_points,
                    bonusPoints: data.bonus_points,
                    totalPoints: data.total_points || ((data.base_points || 0) + (data.bonus_points || 0)),
                    isMilestone: data.is_milestone
                };
            }
            return { success: false, reason: data?.reason, streakDay: data?.streak_day };
        } catch (err: any) {
            console.error('performDailyCheckin error:', err);
            return { success: false, reason: err.message };
        }
    }, [user.id, fetchUserPoints]);

    const recordLoginStreak = useCallback(async () => {
        if (!user.id) return { success: false, reason: 'not_authenticated' };
        try {
            const localDate = new Date().toLocaleDateString('en-CA');
            const { data, error } = await supabase.rpc('record_login_streak', {
                p_user_id: user.id,
                p_client_date: localDate
            });
            if (error) {
                console.error('recordLoginStreak RPC error:', error);
                return { success: false, reason: error.message };
            }
            if (data?.success) {
                await fetchUserPoints();
                await fetchBadgeStatus();
                return {
                    success: true,
                    streak: data.streak,
                    points: data.points
                };
            }
            return { success: false, reason: data?.reason, streak: data?.streak };
        } catch (err: any) {
            console.error('recordLoginStreak error:', err);
            return { success: false, reason: err.message };
        }
    }, [user.id, fetchUserPoints]);

    const getCheckinStatus = useCallback(async () => {
        if (!user.id) return { checkedInToday: false, currentStreak: 0, monthCheckins: 0, monthCalendar: [], repairCost: 100 };
        try {
            const localToday = new Date().toLocaleDateString('en-CA');
            const { data, error } = await supabase.rpc('get_checkin_status', {
                p_user_id: user.id,
                p_client_date: localToday
            });
            if (error) {
                console.error('getCheckinStatus RPC error:', error);
                return { checkedInToday: false, currentStreak: 0, monthCheckins: 0, monthCalendar: [], repairCost: 100 };
            }

            // Client-side timezone-safe check: does today's LOCAL date exist in the calendar?
            const calendar = data?.month_calendar || [];
            const todayRecord = calendar.find((d: any) => d.date?.startsWith(localToday));
            const checkedInToday = !!todayRecord || (data?.checked_in_today || false);

            // Display streak: prefer server's dynamically-calculated current_streak
            // Only fall back to calendar data when server returns 0 due to UTC timezone mismatch
            let displayStreak = data?.current_streak || 0;
            if (displayStreak === 0 && checkedInToday && todayRecord) {
                // Server returned 0 (UTC thinks it's next day), use calendar record
                displayStreak = todayRecord.streak_day || 0;
            }
            if (displayStreak === 0 && !checkedInToday) {
                // Not checked in yet today — show yesterday's streak
                const yesterday = new Date();
                yesterday.setDate(yesterday.getDate() - 1);
                const yStr = yesterday.toLocaleDateString('en-CA');
                const yRecord = calendar.find((d: any) => d.date?.startsWith(yStr));
                if (yRecord) {
                    displayStreak = yRecord.streak_day;
                }
            }

            return {
                checkedInToday: checkedInToday,
                currentStreak: displayStreak,
                monthCheckins: data?.month_checkins || 0,
                monthCalendar: calendar,
                repairCost: data?.next_repair_cost || 100
            };
        } catch (err) {
            console.error('getCheckinStatus error:', err);
            return { checkedInToday: false, currentStreak: 0, monthCheckins: 0, monthCalendar: [], repairCost: 100 };
        }
    }, [user.id]);

    const refreshCheckinStatus = useCallback(async () => {
        if (!user.id) return;
        const status = await getCheckinStatus();
        if (status.checkedInToday) {
            setCheckinStatus('checked_in');
            sessionStorage.setItem('streak_checked_today', 'true');
        } else {
            setCheckinStatus('not_checked_in');
            sessionStorage.removeItem('streak_checked_today');
        }
    }, [user.id, getCheckinStatus]);

    useEffect(() => {
        if (user.id) refreshCheckinStatus();
    }, [user.id]);

    const markProIntroSeen = async () => {
        if (!isAuthenticated || !user.id) return;

        console.log('[ProIntro] Marking as seen for user:', user.id);

        try {
            const { error } = await supabase
                .from('user_profiles')
                .update({ has_seen_pro_intro: true })
                .eq('id', user.id);

            if (error) {
                console.error('[ProIntro] Database update failed:', error);
                throw error;
            }

            console.log('[ProIntro] Database updated successfully');

            setUser(prev => {
                const updated = { ...prev, hasSeenProIntro: true };
                localStorage.setItem('user_profile_cache', JSON.stringify(updated));
                console.log('[ProIntro] Local state and cache updated');
                return updated;
            });
        } catch (error) {
            console.error('[ProIntro] Failed to mark pro intro as seen:', error);
        }
    };

    const logout = async () => {
        try {
            await supabase.auth.signOut();
        } catch (error) {
            console.error('Error signing out:', error);
        }

        // Reset app state first
        setIsAuthenticated(false);
        setIsCreatorAuthenticated(false);
        setUser(INITIAL_USER);
        setUserPoints({ balance: 0, lifetimeEarned: 0 });
        setSectionProgressMap({});
        setFriendIds(new Set());

        // Clear all storage (sessionStorage.clear() already removes streak_checked_today)
        localStorage.clear();
        sessionStorage.clear();

        // Mark that the login prompt has been dismissed for this new session
        sessionStorage.setItem('hasDismissedLoginPrompt', 'true');

        window.location.replace(window.location.origin + '/#/');
    };

    const toggleCourse = (course: CourseType) => {
        localStorage.setItem('user_course_mode', course);
        setUser(prev => ({ ...prev, currentCourse: course }));
    };

    const startCourse = (courseId: CourseType) => {
        setCourses(prev => ({
            ...prev,
            [courseId]: {
                ...prev[courseId],
                status: 'In Progress'
            }
        }));
        setUser(prev => ({ ...prev, currentCourse: courseId }));
        localStorage.setItem('user_course_mode', courseId);
    };

    const updateUser = (updates: Partial<User>) => {
        setUser(prev => {
            const newState = { ...prev, ...updates };

            // Check if name is being updated, we do NOT regenerate avatar here anymore 
            // as it was destructively overwriting custom user-uploaded avatars whenever 
            // the settings page was saved.

            // Sync to DB if authenticated
            if (prev.id) {
                // Prepare sync object with all profile fields for the new Node.js backend API
                const syncData: any = {
                    name: newState.name,
                    avatar_url: newState.avatarUrl,
                    subscription_tier: newState.subscriptionTier,
                    subscription_period_end: newState.subscriptionPeriodEnd,
                    has_seen_pro_intro: newState.hasSeenProIntro,
                    bio: newState.bio,
                    avatar_color: newState.avatarColor,
                    show_name: newState.showName,
                    show_email: newState.showEmail,
                    show_bio: newState.showBio,
                    equipped_title_id: newState.equippedTitleId,
                    email_notifications: newState.preferences?.emailNotifications,
                    sound_effects: newState.preferences?.soundEffects,
                    has_claimed_welcome_gift: newState.hasClaimedWelcomeGift,
                    // Prestige and titles
                    selected_prestige_level: newState.selectedPrestigeLevel,
                    show_prestige: newState.showPrestige,
                    // Practice mode lock
                    locked_practice_mode: newState.lockedPracticeMode,
                    practice_mode_locked_at: newState.practiceModLockedAt
                };

                // Use the backend API to securely update profile (bypassing Client RLS)
                usersApi.updateProfile(syncData)
                    .catch(error => console.error('Failed to sync profile via Backend API:', error));
            }

            localStorage.setItem('user_profile_cache', JSON.stringify(newState));
            return newState;
        });
    };

    // Helper: check if practice mode is currently locked
    const isModeLocked = useMemo(() => {
        if (!user.lockedPracticeMode || !user.practiceModLockedAt) return false;
        const lockedAt = new Date(user.practiceModLockedAt);
        const sixMonthsLater = new Date(lockedAt);
        sixMonthsLater.setMonth(sixMonthsLater.getMonth() + 6);
        return new Date() < sixMonthsLater;
    }, [user.lockedPracticeMode, user.practiceModLockedAt]);

    const lockedModeExpiry = useMemo(() => {
        if (!user.practiceModLockedAt) return null;
        const lockedAt = new Date(user.practiceModLockedAt);
        const sixMonthsLater = new Date(lockedAt);
        sixMonthsLater.setMonth(sixMonthsLater.getMonth() + 6);
        return sixMonthsLater.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }, [user.practiceModLockedAt]);

    // Lock practice mode for 6 months (called from onboarding modal)
    const lockPracticeMode = async (mode: 'Adaptive' | 'Review' | 'Random') => {
        const now = new Date().toISOString();
        setUser(prev => {
            const updated = { ...prev, lockedPracticeMode: mode, practiceModLockedAt: now };
            localStorage.setItem('user_profile_cache', JSON.stringify(updated));
            return updated;
        });
        // Set the recommendation mode immediately
        setRecommendation(prev => ({ ...prev, mode }));
        // Persist to DB
        if (user.id) {
            try {
                await usersApi.updateProfile({
                    locked_practice_mode: mode,
                    practice_mode_locked_at: now
                });
            } catch (e) {
                console.error('Failed to lock practice mode:', e);
            }
        }
    };

    // Bug1 fix: setSessionMode recalculates weightedScore with mode-dependent weights
    const setSessionMode = (mode: SessionMode) => {
        // If mode is locked, reject the switch
        if (isModeLocked && user.lockedPracticeMode && mode !== user.lockedPracticeMode) {
            console.warn(`[Mode Lock] Cannot switch to ${mode} — locked to ${user.lockedPracticeMode}`);
            return;
        }
        setRecommendation(prev => {
            if (!prev.scoringFactors) return { ...prev, mode };
            const W = getModeWeights(mode);
            const f = prev.scoringFactors;
            const weightedScore = W.masteryGap * f.masteryGap
                + W.recencyDecay * f.recencyDecay
                + W.coverageGap * f.coverageGap
                + W.sequentialBonus * f.sequentialBonus
                + W.weakBoost * f.weakBoost;
            const scoreDisplay = Math.round(weightedScore * 100);
            const reason = prev.reason.replace(/\[Score: \d+\]/, `[Score: ${scoreDisplay}]`);
            return { ...prev, mode, weightedScore: Math.round(weightedScore * 1000) / 1000, reason };
        });
    };

    // Allow user to manually switch the recommended unit topic
    // Recomputes all 5 enterprise scoring factors for the selected unit
    const setRecommendationTopic = (topicId: string) => {
        const topics = COURSE_TOPICS[user.currentCourse];
        const selectedTopic = topics.find(t => t.id === topicId);
        if (!selectedTopic) return;

        const index = topics.indexOf(selectedTopic);
        const topicName = topicId.includes('_')
            ? topicId.split('_').slice(1).join('_')
            : topicId;
        const unitLabel = selectedTopic.subject.split(':')[0].trim();
        const radar = radarData.find(r => r.subject === unitLabel);
        const mastery = radar?.A ?? 0;

        // ── Recompute all 5 factors for the selected unit ──

        // Factor 1: MasteryGap (sigmoid-compressed)
        const rawGap = (100 - mastery) / 100;
        const compressedGap = 2 / (1 + Math.exp(-5 * (rawGap - 0.5))) - 1;
        const masteryGap = Math.max(0, Math.min(1, (rawGap + compressedGap) / 2));

        // Factor 2: RecencyDecay (exponential) — Bug6 fix: robust matching
        const matchingProgress = Object.values(sectionProgressMap || {}).filter(
            (p: any) => {
                const st = (p.data?.sessionTopic || '').toLowerCase();
                const sid = (p.section_id || '').toLowerCase();
                const tn = topicName.toLowerCase();
                const tid = topicId.toLowerCase();
                return st === tn || st === tid || st.includes(tn) || tn.includes(st)
                    || sid.includes(tn) || sid.includes(tid);
            }
        );
        const lastAccessedMs = matchingProgress.length > 0
            ? Math.max(...matchingProgress.map((s: any) => new Date(s.last_accessed_at || 0).getTime()))
            : 0;
        const daysSinceLast = lastAccessedMs > 0
            ? (Date.now() - lastAccessedMs) / (1000 * 60 * 60 * 24) : -1;
        const tau = 7;
        const recencyDecay = daysSinceLast < 0
            ? 0.3 : Math.min(1, 1 - Math.exp(-daysSinceLast / tau));

        // Factor 3: CoverageGap
        const unitContent = topicContent[topicId]
            || Object.values(topicContent).find(u => u.id === topicId || u.id.includes(topicName));
        const totalSubTopics = unitContent?.subTopics?.length || 1;
        const completedSections = matchingProgress.filter(
            (s: any) => s.status === 'completed' || s.correct_questions > 0
        ).length;
        const coverageGap = Math.max(0, 1 - completedSections / totalSubTopics);

        // Factor 4: SequentialBonus
        let sequentialBonus = 0;
        if (mastery === 0 && index > 0) {
            const prevLabel = topics[index - 1].subject.split(':')[0].trim();
            const prevMastery = radarData.find(r => r.subject === prevLabel)?.A ?? 0;
            if (prevMastery >= 1) sequentialBonus = 0.6 * (1 - index / topics.length);
        } else if (mastery === 0 && index === 0) {
            sequentialBonus = 0.8;
        }

        // Factor 5: WeakBoost
        const hasStarted = mastery > 0 || matchingProgress.length > 0;
        const weakBoost = hasStarted && mastery < 50 ? 0.3 * (1 - mastery / 50) : 0;

        // ─ Penalty: Strict Prerequisite Blocker ─
        let preReqPenalty = 1.0;
        if (index > 0) {
            const prevLabel = topics[index - 1].subject.split(':')[0].trim();
            const prevMastery = radarData.find(r => r.subject === prevLabel)?.A ?? 0;
            if (prevMastery < 40) {
                preReqPenalty = 0.1;
            }
        }

        // Composite score — use mode-dependent weights
        const currentMode = recommendation.mode; // Bug2 fix: preserve user's mode
        const W = getModeWeights(currentMode);
        const rawWeightedScore =
            W.masteryGap * masteryGap + W.recencyDecay * recencyDecay + W.coverageGap * coverageGap
            + W.sequentialBonus * sequentialBonus + W.weakBoost * weakBoost;
        const weightedScore = rawWeightedScore * preReqPenalty;

        // Confidence assessment
        const totalSessions = Object.values(sectionProgressMap || {}).length;
        const unitsWithData = radarData.filter(r => r.A > 0).length;
        const confidenceLevel: 'low' | 'medium' | 'high' =
            totalSessions >= 10 && unitsWithData >= 3 ? 'high'
                : totalSessions >= 3 || unitsWithData >= 2 ? 'medium' : 'low';

        // Reason generation
        const scoreDisplay = Math.round(weightedScore * 100);
        let reason: string;
        if (preReqPenalty < 1.0) {
            reason = `${unitLabel} is locked behind prerequisites. Please complete earlier units first. [Score: ${scoreDisplay}]`;
        } else if (!hasStarted) {
            reason = sequentialBonus > 0.3
                ? `${unitLabel} is your next logical step — prerequisite units look solid. [Score: ${scoreDisplay}]`
                : `${unitLabel} hasn't been started. Expanding coverage will strengthen your foundation. [Score: ${scoreDisplay}]`;
        } else if (mastery >= 70) {
            const days = daysSinceLast > 0 ? `${Math.round(daysSinceLast)}d ago` : 'recently';
            reason = `${unitLabel} at ${mastery}% — revisit to maintain mastery (last practiced ${days}). [Score: ${scoreDisplay}]`;
        } else {
            reason = `${unitLabel} at ${mastery}% — highest improvement potential across all factors. [Score: ${scoreDisplay}]`;
        }

        // Bug2 fix: preserve user's manually selected mode instead of overriding
        setRecommendation(prev => ({
            ...prev,
            topic: topicName,
            reason,
            currentMastery: mastery,
            targetMastery: 80,
            mode: prev.mode, // KEEP user's selected mode
            hasData: true,
            weightedScore: Math.round(weightedScore * 1000) / 1000,
            confidenceLevel,
            scoringFactors: {
                masteryGap: Math.round(masteryGap * 1000) / 1000,
                recencyDecay: Math.round(recencyDecay * 1000) / 1000,
                coverageGap: Math.round(coverageGap * 1000) / 1000,
                sequentialBonus: Math.round(sequentialBonus * 1000) / 1000,
                weakBoost: Math.round(weakBoost * 1000) / 1000,
            },
        }));
    };

    const dismissLoginPrompt = () => {
        setHasDismissedLoginPrompt(true);
        sessionStorage.setItem('hasDismissedLoginPrompt', 'true');
    };

    // Creator Area Logic - Syncs to Supabase via Backend API
    const addQuestion = async (q: Partial<Question> & { options: any[] }) => {
        try {
            // Call backend API to create question in Supabase
            const newQuestion = await questionsApi.createQuestion({
                ...q,
                topic: q.topic,
                topicId: (q as any).topicId,
                subTopicId: q.subTopicId,
                primarySkillId: (q as any).primarySkillId,
                supportingSkillIds: (q as any).supportingSkillIds || [],
            });

            // Update local state with the returned question
            setQuestions(prev => [...prev, newQuestion]);
            console.log('✅ Question created and synced to Supabase:', newQuestion.id);
            return true;
        } catch (error: any) {
            console.error('Failed to create question:', error);
            throw error; // Propagate to caller for handling
        }
    };

    const updateQuestion = async (updatedQ: Question) => {
        try {
            // Optimistic update
            setQuestions(prev => prev.map(q => q.id === updatedQ.id ? updatedQ : q));

            // Call backend API to update in Supabase
            await questionsApi.updateQuestion(updatedQ.id!, {
                ...updatedQ,
                topicId: (updatedQ as any).topicId,
                primarySkillId: (updatedQ as any).primarySkillId,
                supportingSkillIds: (updatedQ as any).supportingSkillIds || [],
            });
            console.log('✅ Question updated and synced to Supabase:', updatedQ.id);
            return true;
        } catch (error: any) {
            console.error('Failed to update question:', error);
            throw error; // Propagate to caller
        }
    };

    const deleteQuestion = async (id: string) => {
        try {
            // Optimistic delete
            setQuestions(prev => prev.filter(q => q.id !== id));

            // Call backend API to delete from Supabase
            await questionsApi.deleteQuestion(id);
            console.log('✅ Question deleted from Supabase:', id);
            return true;
        } catch (error: any) {
            console.error('Failed to delete question:', error);
            throw error;
        }
    };

    const updateTopic = async (unitId: string, subTopicId: string | null, data: any) => {
        // 1. Optimistic Update (UI updates immediately)
        setTopicContent(prev => {
            const next = { ...prev };
            const unit = next[unitId];

            if (!unit) return next;

            if (subTopicId === 'unit_test') {
                const currentConfig = unit.unitTest || {
                    title: 'Unit Test',
                    description: `Comprehensive assessment covering all topics in ${unit.title}.`,
                    estimatedMinutes: 45
                };
                next[unitId] = { ...unit, unitTest: { ...currentConfig, ...data } };
            } else if (subTopicId) {
                const updateSubInList = (u: UnitContent) => {
                    const idx = u.subTopics.findIndex(s => s.id === subTopicId);
                    if (idx !== -1) {
                        const newSubs = [...u.subTopics];
                        newSubs[idx] = { ...newSubs[idx], ...data };
                        return { ...u, subTopics: newSubs };
                    }
                    return u;
                };

                // Update specific unit and propagate to shared units (e.g. AB/BC sharing)
                next[unitId] = updateSubInList(unit);
                for (const key in next) {
                    if (key !== unitId) next[key] = updateSubInList(next[key]);
                }
            } else {
                next[unitId] = { ...unit, ...data };
            }

            return next;
        });

        // 2. Persist to Backend
        try {
            if (subTopicId) {
                // Update SubTopic or Unit Test
                await contentApi.updateSubTopic(unitId, subTopicId, data);
            } else {
                // Update Unit Metadata
                await contentApi.updateTopic(unitId, data);
            }
            console.log('✅ Topic settings saved to database');
        } catch (error) {
            console.error('❌ Failed to save topic settings:', error);
            // Ideally revert state here or show toast, but keeping simple for now
        }
    };

    const verifyAccessCode = async (code: string): Promise<{ success: boolean; message?: string }> => {
        try {
            const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/verify-access-code`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code, userId: user.id })
            });
            const data = await response.json();

            if (data.success) {
                // Update local state immediately
                setUser(prev => ({ ...prev, isCreator: true }));
                setIsCreatorAuthenticated(true); // Keep legacy state for now just in case
                return { success: true };
            }
            return { success: false, message: data.error || 'Verification failed' };
        } catch (error: any) {
            console.error('Verify access code error:', error);
            return { success: false, message: error.message || 'Network error' };
        }
    };

    // Notification Methods moved to top


    const completePractice = ({ correct, total, topic, isReview, newlyCorrectFirstAttempts }: { correct: number; total: number; topic: string; isReview?: boolean; newlyCorrectFirstAttempts?: number }) => {
        // 5. Send to backend APIs (includes unit_mastery logic from route)
        practiceApi.completePractice({ correct, total, topic, isReview, newlyCorrectFirstAttempts })
            .catch(console.error);
        // 1. Update User Stats
        setUser(prev => {
            const newFirstTimeCorrects = newlyCorrectFirstAttempts !== undefined ? newlyCorrectFirstAttempts : correct;
            const newSolved = isReview ? prev.problemsSolved : prev.problemsSolved + newFirstTimeCorrects;
            // Simple mock mastery calculation logic (starts aggressive for new users)
            const performanceFactor = newFirstTimeCorrects / Math.max(1, total);

            // Add study time to "Today" dynamically (Sun=0, Sat=6)
            const todayIndex = new Date().getDay();
            const newStudyHours = [...prev.studyHours];
            newStudyHours[todayIndex] = parseFloat((newStudyHours[todayIndex] + 0.2).toFixed(1)); // Add 0.2 hours per session

            // Improve percentile from 0 if new (do not improve if review)
            const newPercentile = isReview ? prev.percentile : (prev.percentile === 0 ? 50 : Math.max(1, prev.percentile - (performanceFactor > 0.5 ? 1 : 0)));

            return {
                ...prev,
                problemsSolved: newSolved,
                // masteryRate is now derived from getCourseMastery in UI, but we keep this for legacy or specific use
                masteryRate: prev.masteryRate,
                studyHours: newStudyHours,
                percentile: newPercentile
            };
        });

        // 2. Add Activity
        if (!isReview) {
            const newActivity: Activity = {
                id: Date.now(),
                type: 'practice',
                title: `Practice: ${topic.includes('_') ? topic.split('_')[1] : topic}`, // Clean title for display
                description: `Solved ${correct}/${total} problems correctly.`,
                timestamp: 'Just now',
                score: Math.round((correct / total) * 100)
            };
            setActivities(prev => [newActivity, ...prev]);
        }

        // 3. Update Radar Data for Topic
        // Note: The 'topic' string passed here must match 'subject' in radarData (e.g., 'Limits', not 'AB_Limits')
        // QuestionCreator and Practice logic ensure we pass the clean name.
        setRadarData(prev => prev.map(item => {
            const cleanIncoming = topic.includes('_') ? topic.split('_')[1] : topic;
            let expectedLabel = '';
            if (cleanIncoming.includes('Limits')) expectedLabel = 'Unit 1';
            else if (cleanIncoming.includes('Derivatives')) expectedLabel = 'Unit 2';
            else if (cleanIncoming.includes('Composite')) expectedLabel = 'Unit 3';
            else if (cleanIncoming.includes('Applications')) expectedLabel = 'Unit 4';
            else if (cleanIncoming.includes('Analytical')) expectedLabel = 'Unit 5';
            else if (cleanIncoming.includes('Integration')) expectedLabel = 'Unit 6';
            else if (cleanIncoming.includes('DiffEq')) expectedLabel = 'Unit 7';
            else if (cleanIncoming.includes('AppIntegration')) expectedLabel = 'Unit 8';
            else if (cleanIncoming.includes('Unit9') || cleanIncoming.includes('Parametric')) expectedLabel = 'Unit 9';
            else if (cleanIncoming.includes('Series')) expectedLabel = 'Unit 10';

            if (item.subject === expectedLabel) {
                // Return item as is, but we could do a local update if we wanted.
                // However, the user wants accuracy, so we rely on fetchRadarData which is called later.
                return item;
            }
            return item;
        }));

        // 4. Update Line Chart
        setLineData(prev => {
            const newData = [...prev];
            const lastEntry = newData[newData.length - 1];
            // Bump today's value based on activity
            newData[newData.length - 1] = { ...lastEntry, value: Math.min(100, lastEntry.value + 5) };
            return newData;
        });

        // 5. Update Course Status (Just ensure it's 'In Progress')
        setCourses(prev => {
            const courseId = user.currentCourse;
            return {
                ...prev,
                [courseId]: {
                    ...prev[courseId],
                    status: 'In Progress'
                }
            };
        });

        // 6. Badges are handled persistently now. We can trigger a refresh if needed.
        fetchBadgeStatus();

        // 7. Refresh Radar/Mastery from Database to ensure accuracy
        fetchRadarData();
        fetchLineData();
        fetchRecentActivities();
    };

    return (
        <AppContext.Provider value={{
            user,
            isAuthenticated,
            isAuthLoading,
            activities,
            radarData,
            lineData,
            accuracyHistory,
            courses,
            recommendation,
            hasDismissedLoginPrompt,
            questions,
            isCreatorAuthenticated,
            topicContent,
            skills,
            sections,
            userInsights,
            reviewQueue,
            login,
            logout,
            toggleCourse,
            startCourse,
            updateUser,
            completePractice,
            setSessionMode,
            setRecommendationTopic,
            lockPracticeMode,
            isModeLocked,
            lockedModeExpiry,
            dismissLoginPrompt,
            availableTitles,
            addQuestion,
            updateQuestion,
            deleteQuestion,
            updateTopic,
            updateSection,
            verifyAccessCode,
            navRedDots,
            fetchBadgeStatus,
            markBadgeAsRead,
            clearBadgeLocally,
            sendGlobalBroadcast,
            acceptFriendRequest,
            getCourseMastery,
            getSectionsForTopic,
            fetchSections,
            fetchQuestions,
            submitAttempt,
            getUserInsights,
            getReviewQueue,
            toggleQuestionStarred,
            toggleQuestionFlagged,
            getTopicProgress,
            saveSectionProgress,
            completeSectionSession,
            getUnitProgress,
            getCourseProgress,
            getSectionProgress,
            getTopicSectionProgress,
            fetchAllUserProgress,
            sectionProgressMap,
            getSectionStatus,
            getSectionProgressData,
            resetSectionProgress,
            softResetSectionProgress,
            sectionTimes,
            incorrectQuestionIds,
            fetchIncorrectQuestions,
            diagSupabase,
            logUserActivity,
            getUserActivities,
            fetchAccuracyHistory,
            friendIds,
            fetchFriends,
            newlyUnlockedTitle,
            setNewlyUnlockedTitle,
            isPro,
            claimFreePro,
            markProIntroSeen,
            showPaywall,
            setShowPaywall,
            unreadCounts,
            clearUnread,
            // Points Economy
            userPoints,
            pointsBalanceRef,
            awardPoints,
            spendPoints,
            fetchGameStats,
            saveGameStats,
            triggerCoinAnimation,
            redeemProWithPoints,
            performDailyCheckin,
            recordLoginStreak,
            getCheckinStatus,
            fetchUserPoints,
            recentPointsTransaction,
            isStreakModalOpen,
            setIsStreakModalOpen,

            checkinStatus,
            refreshCheckinStatus,
            // Prestige
            userPrestige,
            fetchUserPrestige,
            purchaseStardust,
            injectStardust,
            wrongAnswersCache,
            fetchWrongAnswersCache
        }}>
            {children}
        </AppContext.Provider>
    );
};

export const useApp = () => {
    const context = useContext(AppContext);
    if (context === undefined) {
        throw new Error('useApp must be used within an AppProvider');
    }
    return context;
};
